import maplibregl from "maplibre-gl";
import { createMap, type MapHandle } from "../map/mapView";
import {
  RouteEditor,
  type RouteEditorState,
} from "../map/routeEditor";
import { RouteLogOverlay } from "../map/routeLogOverlay";
import type { PackRouter } from "../map/packRouter";
import {
  chartServer,
  formatBytes,
  normaliseChartServer,
  packTotalBytes,
  type PackDescriptor,
} from "../storage/manifest";
import {
  searchStoredMaps,
  type SearchFeatureKind,
  type SearchResult,
} from "../storage/search";
import {
  attachTracking,
  type TrackingHandle,
  type TrackingStatus,
} from "../location/tracking";
import {
  requestPwaInstall,
  subscribeInstallState,
  type PwaInstallState,
} from "../pwa/install";
import {
  setDarkMode,
  normaliseOwnMmsi,
  setProviderUrls,
  setOwnMmsi,
  setUiTextSize,
  setWindSpeedUnit,
  uiPreferences,
  type UiTextSize,
  type WindSpeedUnit,
} from "./preferences";
import {
  fetchForecastWind,
  fetchLatestWindReadings,
  interpolateWind,
  type ForecastWindSeries,
  type WindReading,
} from "../weather/windData";
import {
  WindOverlay,
  windGridLocations,
  type WindDisplaySample,
} from "../weather/windOverlay";
import {
  aisDataAvailable,
  aisDataSource,
  aisDataSourceLabel,
  fetchAisTrack,
  fetchLatestAis,
  fetchTypicalAis,
  fetchTypicalAisSnapshot,
  type AisPointCollection,
  type AisBounds,
} from "../ais/aisData";
import {
  AisOverlay,
  type AisVesselSelection,
} from "../ais/aisOverlay";
import {
  collisionRisks,
  typicalCoursesByVessel,
} from "../ais/collisionRisk";
import {
  bearingToPoint,
  deleteRoute,
  kilometresToNauticalMiles,
  loadRoutes,
  pointDistanceKm,
  routeDistanceKm,
  routeUrl,
  saveRoute,
  type RouteDefinition,
  type RoutePoint,
  type StoredRoute,
} from "../routes/routes";
import {
  appendRouteLogMotorEvent,
  appendRouteLogSample,
  deleteRouteLog,
  finishRouteLog,
  loadRouteLogSamples,
  loadRouteLogs,
  recordManOverboard,
  startRouteLog,
  type LoggedWind,
  type RouteLog,
  type RouteLogSample,
} from "../routes/routeLogs";

interface MapScreenOptions {
  styleUrl: string;
  router: PackRouter;
  fallbackCenter: [number, number];
  searchPacks: PackDescriptor[];
  hasCatalogue: boolean;
  initialRoute: RouteDefinition | null;
  onMapServerChange: (address: string | null) => Promise<void>;
  onManageCharts: () => void;
  onShareCharts: () => void;
}

type MenuPanel =
  | "main"
  | "maps"
  | "layers"
  | "routes"
  | "savedRoutes"
  | "routeLogs"
  | "wind"
  | "ais"
  | "settings";
type WindMode = "off" | "live" | "forecast";
type AisMode = "off" | "live" | "historical";

const LIVE_WIND_REFRESH_MS = 10 * 60 * 1000;
const LIVE_AIS_REFRESH_MS = 30 * 1000;
const MAX_LIVE_STATION_DISTANCE_KM = 600;
const ROUTE_ARRIVAL_KM = 0.1;
const FRESH_POSITION_MS = 30_000;
const MAP_LONG_PRESS_MS = 600;
const MAP_LONG_PRESS_TOLERANCE_PX = 12;
const TYPICAL_AIS_CACHE_MS = 10 * 60 * 1000;
const KNOTS_PER_METRE_PER_SECOND = 1.943_844_492_440_6;
const COLLISION_AIS_REFRESH_MS = 30 * 1000;
const COLLISION_ASSUMED_TARGET_SPEED_KNOTS = 50;

function localDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateFromNow(days: number): string {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return localDateValue(date);
}

function routeLogDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function routeLogDuration(startedAt: number, endedAt: number | null): string {
  if (endedAt === null) return "Not finished";
  const totalMinutes = Math.max(0, Math.round((endedAt - startedAt) / 60_000));
  if (totalMinutes < 1) return "Less than a minute";
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
}

function forecastTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Wind data could not be loaded.";
}

const SEARCH_KIND_LABELS: Record<SearchFeatureKind, string> = {
  city: "City",
  town: "Town",
  village: "Village",
  locality: "Locality",
  water: "Named water",
  sea: "Sea area",
  land: "Island or land area",
  region: "Region",
  harbour: "Harbour",
  light: "Light",
  "navigation-mark": "Navigation mark",
  wreck: "Wreck",
  rock: "Rock",
  anchorage: "Anchorage",
  "map-feature": "Map feature",
  "chart-feature": "Chart feature",
};

function countryName(code: string | undefined): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames(undefined, { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

function searchResultDetail(result: SearchResult, fallback: string): string {
  const parts: string[] = [];
  if (result.kind) parts.push(SEARCH_KIND_LABELS[result.kind]);
  if (result.areaName) parts.push(result.areaName);
  if (result.nearbyName) parts.push(`near ${result.nearbyName}`);
  const country = countryName(result.countryCode);
  if (country) parts.push(country);
  return parts.length ? parts.join(" · ") : fallback;
}

function searchResultKey(result: SearchResult): string {
  return `${result.packId}:${result.longitude}:${result.latitude}:${result.name}`;
}

export class MapScreen {
  private readonly root = document.createElement("div");
  private readonly canvas = document.createElement("div");
  private readonly banner = document.createElement("p");
  private readonly menu = document.createElement("aside");
  private readonly backdrop = document.createElement("button");
  private readonly mainPanel = document.createElement("section");
  private readonly mapsPanel = document.createElement("section");
  private readonly layersPanel = document.createElement("section");
  private readonly routesPanel = document.createElement("section");
  private readonly savedRoutesPanel = document.createElement("section");
  private readonly routeLogsPanel = document.createElement("section");
  private readonly windPanel = document.createElement("section");
  private readonly aisPanel = document.createElement("section");
  private readonly settingsPanel = document.createElement("section");
  private readonly layerList = document.createElement("div");
  private readonly installNote = document.createElement("p");
  private readonly routePanel = document.createElement("aside");
  private readonly routeTitle = document.createElement("h2");
  private readonly routeEditorContent = document.createElement("div");
  private readonly routeReadout = document.createElement("output");
  private readonly routeInstruction = document.createElement("p");
  private readonly routeShareBox = document.createElement("div");
  private readonly routeShareInput = document.createElement("input");
  private readonly routeNavigationChoice = document.createElement("div");
  private readonly routeNavigationDisplay = document.createElement("div");
  private readonly routeNavigationLabel = document.createElement("span");
  private readonly routeNavigationBearing = document.createElement("output");
  private readonly routeNavigationDetail = document.createElement("p");
  private readonly routeNavigationStatus = document.createElement("p");
  private readonly routeNavigationNextButton = document.createElement("button");
  private readonly routeNavigationBackButton = document.createElement("button");
  private readonly routeLogControl = document.createElement("aside");
  private readonly routeLogControlStatus = document.createElement("output");
  private readonly routeLogMotorButton = document.createElement("button");
  private readonly routeLogMenuStatus = document.createElement("p");
  private readonly routeLogList = document.createElement("div");
  private readonly routeLogViewControl = document.createElement("aside");
  private readonly routeLogViewTitle = document.createElement("strong");
  private readonly routeLogViewDetail = document.createElement("span");
  private readonly routeNameDialog = document.createElement("dialog");
  private readonly routeNameInput = document.createElement("input");
  private readonly savedRouteList = document.createElement("div");
  private readonly searchInput = document.createElement("input");
  private readonly searchResults = document.createElement("div");
  private readonly windStatus = document.createElement("p");
  private readonly forecastDateInput = document.createElement("input");
  private readonly forecastDateSetting = document.createElement("label");
  private readonly windTimeControl = document.createElement("aside");
  private readonly windTimeSlider = document.createElement("input");
  private readonly windTimeLabel = document.createElement("output");
  private readonly aisStatus = document.createElement("p");
  private readonly aisExplanation = document.createElement("p");
  private readonly aisHistoryDaysSelect = document.createElement("select");
  private readonly aisTimeControl = document.createElement("aside");
  private readonly aisTimeSlider = document.createElement("input");
  private readonly aisTimeLabel = document.createElement("output");
  private readonly collisionWarning = document.createElement("aside");
  private readonly collisionWarningTitle = document.createElement("strong");
  private readonly collisionWarningDetail = document.createElement("span");
  private handle: MapHandle | null = null;
  private stopped = false;
  private searchSequence = 0;
  private searchTimer: number | null = null;
  private searchPacks: PackDescriptor[];
  private searchUpdating = false;
  private currentSearchResults: SearchResult[] = [];
  private selectedSearchKey: string | null = null;
  private installButton: HTMLButtonElement | null = null;
  private newRouteButton: HTMLButtonElement | null = null;
  private routeLogButton: HTMLButtonElement | null = null;
  private manOverboardButton: HTMLButtonElement | null = null;
  private routeUndoButton: HTMLButtonElement | null = null;
  private routeClearButton: HTMLButtonElement | null = null;
  private routeSaveButton: HTMLButtonElement | null = null;
  private routeShareButton: HTMLButtonElement | null = null;
  private routeNavigateButton: HTMLButtonElement | null = null;
  private routeEditor: RouteEditor | null = null;
  private routeState: RouteEditorState | null = null;
  private activeRouteId: string | null = null;
  private activeRouteName = "New route";
  private routeNavigating = false;
  private routeTargetIndex: number | null = null;
  private routeDirectionStep: 1 | -1 = 1;
  private routeTargetNeedsSelection = false;
  private manOverboardNavigation = false;
  private pendingManOverboard = false;
  private manOverboardTimer: number | null = null;
  private tracking: TrackingHandle | null = null;
  private lastTrackingStatus: TrackingStatus = { state: "off" };
  private activeRouteLog: RouteLog | null = null;
  private routeLogOverlay: RouteLogOverlay | null = null;
  private routeLogWindReadings: WindReading[] = [];
  private routeLogWindLabel = "wind pending";
  private routeLogMotorOn = false;
  private routeLogLastTimestamp = 0;
  private routeLogPreviousFix: { point: RoutePoint; recordedAt: number } | null = null;
  private routeLogWrite: Promise<void> = Promise.resolve();
  private routeLogWindAbort: AbortController | null = null;
  private routeLogWindTimer: number | null = null;
  private viewedRouteLogId: string | null = null;
  private routeLogListSequence = 0;
  private windOverlay: WindOverlay | null = null;
  private windMode: WindMode = "live";
  private windReadings: WindReading[] = [];
  private forecastSeries: ForecastWindSeries[] = [];
  private windModeInputs: HTMLInputElement[] = [];
  private windAbort: AbortController | null = null;
  private windRequestSequence = 0;
  private windMoveTimer: number | null = null;
  private liveWindRefreshTimer: number | null = null;
  private aisOverlay: AisOverlay | null = null;
  private aisMode: AisMode = "off";
  private aisModeInputs: HTMLInputElement[] = [];
  private aisTrafficAbort: AbortController | null = null;
  private aisTypicalAbort: AbortController | null = null;
  private collisionAisAbort: AbortController | null = null;
  private aisTrackAbort: AbortController | null = null;
  private aisRequestSequence = 0;
  private aisTrackSequence = 0;
  private aisMoveTimer: number | null = null;
  private aisTimeTimer: number | null = null;
  private liveAisRefreshTimer: number | null = null;
  private selectedAisVessel: AisVesselSelection | null = null;
  private liveAisTraffic: AisPointCollection | null = null;
  private typicalAisTraffic: AisPointCollection | null = null;
  private collisionAisTraffic: AisPointCollection | null = null;
  private collisionTypicalAisTraffic: AisPointCollection | null = null;
  private collisionAisLastRequestAt = 0;
  private collisionAisSequence = 0;
  private typicalAisCache: {
    key: string;
    expiresAt: number;
    collection: AisPointCollection;
  } | null = null;
  private ownMotion: { speedKnots: number; courseDeg: number | null } | null = null;
  private previousOwnMotionFix: { point: RoutePoint; timestamp: number } | null = null;
  private aisTrackDays = 3;
  private aisMockSampleFitted = false;
  private mapLongPressTimer: number | null = null;
  private mapLongPressStart: { x: number; y: number; point: RoutePoint } | null = null;
  private lastMapLongPressAt = 0;
  private unsubscribeInstallState: (() => void) | null = null;

  private readonly onMapContextMenu = (event: maplibregl.MapMouseEvent): void => {
    event.preventDefault();
    event.originalEvent.preventDefault();
    if (Date.now() - this.lastMapLongPressAt < 1_000) return;
    this.startWaypointNavigation([event.lngLat.lng, event.lngLat.lat]);
  };

  private readonly onMapTouchStart = (event: maplibregl.MapTouchEvent): void => {
    this.cancelMapLongPress();
    if (event.points.length !== 1) return;
    this.mapLongPressStart = {
      x: event.point.x,
      y: event.point.y,
      point: [event.lngLat.lng, event.lngLat.lat],
    };
    this.mapLongPressTimer = window.setTimeout(() => {
      const pending = this.mapLongPressStart;
      this.mapLongPressTimer = null;
      this.mapLongPressStart = null;
      if (!pending || this.stopped) return;
      this.lastMapLongPressAt = Date.now();
      this.handle?.map.stop();
      this.startWaypointNavigation(pending.point);
    }, MAP_LONG_PRESS_MS);
  };

  private readonly onMapTouchMove = (event: maplibregl.MapTouchEvent): void => {
    const start = this.mapLongPressStart;
    if (!start || event.points.length !== 1) {
      this.cancelMapLongPress();
      return;
    }
    if (Math.hypot(event.point.x - start.x, event.point.y - start.y) > MAP_LONG_PRESS_TOLERANCE_PX) {
      this.cancelMapLongPress();
    }
  };

  private readonly onMapTouchEnd = (): void => this.cancelMapLongPress();

  private readonly onWindMapMove = (): void => {
    if (this.windMode === "off") return;
    if (this.windMoveTimer !== null) window.clearTimeout(this.windMoveTimer);
    this.windMoveTimer = window.setTimeout(() => {
      this.windMoveTimer = null;
      if (this.windMode === "live") this.renderLiveWind();
      else if (this.windMode === "forecast") void this.loadForecastWind();
    }, 450);
  };

  private readonly onAisMapMove = (): void => {
    if (this.aisMode === "off") return;
    if (this.aisMoveTimer !== null) window.clearTimeout(this.aisMoveTimer);
    this.aisMoveTimer = window.setTimeout(() => {
      this.aisMoveTimer = null;
      if (this.aisMode === "live") void this.loadLiveAis();
      else if (this.aisMode === "historical") void this.loadHistoricalAis();
    }, 450);
  };

  constructor(private readonly options: MapScreenOptions) {
    this.searchPacks = options.searchPacks;
    this.root.className = "map-screen";
    this.canvas.id = "map";

    const overlay = document.createElement("div");
    overlay.className = "map-overlay";

    const menuButton = document.createElement("button");
    menuButton.className = "chip map-menu-button";
    menuButton.type = "button";
    menuButton.setAttribute("aria-haspopup", "dialog");
    menuButton.setAttribute("aria-expanded", "false");
    const menuIcon = document.createElement("span");
    menuIcon.className = "menu-icon";
    menuIcon.setAttribute("aria-hidden", "true");
    menuIcon.textContent = "☰";
    const menuLabel = document.createElement("span");
    menuLabel.textContent = "Menu";
    menuButton.append(menuIcon, menuLabel);
    menuButton.addEventListener("click", () => this.setMenuOpen(true));

    this.banner.className = "banner";
    this.banner.hidden = true;

    overlay.append(menuButton, this.banner);
    this.buildMenu(menuButton);
    this.buildRoutePanel();
    this.buildRouteLogControl();
    this.buildRouteLogViewControl();
    this.buildAisTimeControl();
    overlay.append(this.routeLogControl, this.routeLogViewControl, this.aisTimeControl);
    this.buildRouteNameDialog();
    this.buildWindTimeControl();
    this.buildCollisionWarning();
    this.root.append(
      this.canvas,
      overlay,
      this.collisionWarning,
      this.routePanel,
      this.windTimeControl,
      this.backdrop,
      this.menu,
      this.routeNameDialog,
    );
    this.root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.routeEditor && this.menu.hidden) {
        if (this.routeNavigating) this.endRouteNavigation();
        else this.endRoute();
      }
    });
  }

  private buildCollisionWarning(): void {
    this.collisionWarning.className = "collision-warning";
    this.collisionWarning.hidden = true;
    this.collisionWarning.setAttribute("role", "alert");
    this.collisionWarning.setAttribute("aria-live", "assertive");
    this.collisionWarningTitle.textContent = "Projected collision risk";
    this.collisionWarning.append(this.collisionWarningTitle, this.collisionWarningDetail);
  }

  get element(): HTMLElement {
    return this.root;
  }

  async start(): Promise<void> {
    const handle = await createMap(
      this.canvas,
      this.options.styleUrl,
      this.options.router,
      this.options.fallbackCenter,
    );
    if (this.stopped) {
      handle.map.remove();
      this.options.router.unregister();
      return;
    }
    this.handle = handle;
    this.windOverlay = new WindOverlay(handle.map);
    this.aisOverlay = new AisOverlay(handle.map, {
      onSelect: (vessel) => this.selectAisVessel(vessel),
      onClose: () => this.clearAisSelection(),
      onDaysChange: (days) => this.setAisTrackDays(days),
    });
    this.routeLogOverlay = new RouteLogOverlay(handle.map);
    handle.map.on("moveend", this.onWindMapMove);
    handle.map.on("moveend", this.onAisMapMove);
    handle.map.on("contextmenu", this.onMapContextMenu);
    handle.map.on("touchstart", this.onMapTouchStart);
    handle.map.on("touchmove", this.onMapTouchMove);
    handle.map.on("touchend", this.onMapTouchEnd);
    handle.map.on("touchcancel", this.onMapTouchEnd);
    if (this.newRouteButton) this.newRouteButton.disabled = false;
    if (this.routeLogButton) this.routeLogButton.disabled = false;
    if (this.manOverboardButton) this.manOverboardButton.disabled = false;
    for (const input of this.windModeInputs) input.disabled = false;
    for (const input of this.aisModeInputs) input.disabled = !aisDataAvailable();
    this.renderLayers();
    this.setWindMode("live");
    this.setAisMode("off");

    // The native MapLibre location button is the one and only position action.
    // This wrapper retains the wake lock and useful error banner around it.
    this.tracking = attachTracking(handle.geolocate, (status) => this.showStatus(status));
    if (this.options.initialRoute) {
      this.openRoute(this.options.initialRoute, null, true);
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    if (this.windMoveTimer !== null) window.clearTimeout(this.windMoveTimer);
    if (this.liveWindRefreshTimer !== null) window.clearTimeout(this.liveWindRefreshTimer);
    if (this.aisMoveTimer !== null) window.clearTimeout(this.aisMoveTimer);
    if (this.aisTimeTimer !== null) window.clearTimeout(this.aisTimeTimer);
    if (this.liveAisRefreshTimer !== null) window.clearTimeout(this.liveAisRefreshTimer);
    if (this.routeLogWindTimer !== null) window.clearTimeout(this.routeLogWindTimer);
    if (this.manOverboardTimer !== null) window.clearTimeout(this.manOverboardTimer);
    this.cancelMapLongPress();
    this.windAbort?.abort();
    this.aisTrafficAbort?.abort();
    this.aisTypicalAbort?.abort();
    this.collisionAisAbort?.abort();
    this.aisTrackAbort?.abort();
    this.routeLogWindAbort?.abort();
    this.finishRouteLogOnShutdown();
    this.endRoute(false);
    if (this.routeNameDialog.open) this.routeNameDialog.close();
    this.unsubscribeInstallState?.();
    this.unsubscribeInstallState = null;
    if (this.handle) {
      this.handle.map.off("moveend", this.onWindMapMove);
      this.handle.map.off("moveend", this.onAisMapMove);
      this.handle.map.off("contextmenu", this.onMapContextMenu);
      this.handle.map.off("touchstart", this.onMapTouchStart);
      this.handle.map.off("touchmove", this.onMapTouchMove);
      this.handle.map.off("touchend", this.onMapTouchEnd);
      this.handle.map.off("touchcancel", this.onMapTouchEnd);
    }
    this.windOverlay?.destroy();
    this.windOverlay = null;
    this.aisOverlay?.destroy();
    this.aisOverlay = null;
    this.routeLogOverlay = null;
    this.tracking = null;
    this.handle?.map.remove();
    this.options.router.unregister();
  }

  refreshSearchPacks(packs: PackDescriptor[]): void {
    if (this.stopped) return;
    this.searchPacks = packs;
    const value = this.searchInput.value.trim();
    if (value.length >= 2) void this.runSearch(value);
  }

  setSearchUpdating(updating: boolean): void {
    if (this.stopped) return;
    this.searchUpdating = updating;
    const value = this.searchInput.value.trim();
    if (value.length >= 2) void this.runSearch(value);
  }

  private buildSearch(): HTMLFormElement {
    const form = document.createElement("form");
    form.className = "map-search";
    form.setAttribute("role", "search");

    this.searchInput.type = "search";
    this.searchInput.autocomplete = "off";
    this.searchInput.spellcheck = false;
    this.searchInput.placeholder = "search maps";
    this.searchInput.setAttribute("aria-label", "Search maps");
    this.searchInput.setAttribute("aria-controls", "map-search-results");
    this.searchInput.setAttribute("aria-expanded", "false");
    this.searchInput.addEventListener("input", () => this.scheduleSearch());
    this.searchInput.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.searchResults.hidden) {
        event.stopPropagation();
        this.hideSearchResults();
      }
    });

    this.searchResults.id = "map-search-results";
    this.searchResults.className = "map-search-results";
    this.searchResults.setAttribute("role", "listbox");
    this.searchResults.hidden = true;

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.searchResults.querySelector<HTMLButtonElement>("button")?.click();
    });
    form.append(this.searchInput, this.searchResults);
    return form;
  }

  private scheduleSearch(): void {
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    const value = this.searchInput.value.trim();
    if (value.length < 2) {
      this.hideSearchResults();
      return;
    }
    this.searchTimer = window.setTimeout(() => {
      this.searchTimer = null;
      void this.runSearch(value);
    }, 120);
  }

  private async runSearch(value: string): Promise<void> {
    const sequence = ++this.searchSequence;
    const results = await searchStoredMaps(this.searchPacks, value).catch((error) => {
      console.warn("klint: map search failed:", error);
      return [] as SearchResult[];
    });
    if (sequence !== this.searchSequence || this.searchInput.value.trim() !== value) return;
    this.currentSearchResults = results;
    this.selectedSearchKey = results.length === 1 ? searchResultKey(results[0]) : null;
    this.renderSearchResults();
    this.handle?.showSearchResults(
      results.map((result) => ({
        key: searchResultKey(result),
        label: result.name,
        longitude: result.longitude,
        latitude: result.latitude,
      })),
    );
  }

  private renderSearchResults(): void {
    const results = this.currentSearchResults;
    if (!results.length) {
      const empty = document.createElement("p");
      empty.className = "map-search-empty";
      empty.textContent = this.searchUpdating
        ? "Updating offline search…"
        : this.searchPacks.length
          ? "No matching names in stored maps"
          : "No search index is installed for these maps";
      this.searchResults.replaceChildren(empty);
    } else {
      const packs = new Map(this.searchPacks.map((pack) => [pack.id, pack]));
      const rows = results.map((result) => {
        const key = searchResultKey(result);
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("role", "option");
        button.dataset.resultKey = key;
        button.dataset.selected = String(key === this.selectedSearchKey);
        button.setAttribute("aria-selected", String(key === this.selectedSearchKey));
        const name = document.createElement("strong");
        name.textContent = result.name;
        const detail = document.createElement("small");
        detail.textContent = searchResultDetail(
          result,
          packs.get(result.packId)?.label ?? "Stored map",
        );
        button.append(name, detail);
        button.addEventListener("click", () => this.selectSearchResult(result));
        return button;
      });
      this.searchResults.replaceChildren(...rows);
    }
    this.searchResults.hidden = false;
    this.searchInput.setAttribute("aria-expanded", "true");
  }

  private selectSearchResult(result: SearchResult): void {
    const key = searchResultKey(result);
    this.selectedSearchKey = key;
    this.handle?.setLayerVisibility(result.packId, true);
    this.handle?.highlightSearchResult(key);
    this.renderLayers();
    for (const button of this.searchResults.querySelectorAll<HTMLButtonElement>("button")) {
      const selected = button.dataset.resultKey === key;
      button.dataset.selected = String(selected);
      button.setAttribute("aria-selected", String(selected));
    }
  }

  private hideSearchResults(): void {
    this.searchSequence++;
    this.currentSearchResults = [];
    this.selectedSearchKey = null;
    this.searchResults.hidden = true;
    this.searchInput.setAttribute("aria-expanded", "false");
    this.handle?.showSearchResults([]);
  }

  private buildRoutePanel(): void {
    this.routePanel.className = "route-panel";
    this.routePanel.hidden = true;
    this.routePanel.setAttribute("aria-label", "Route editor");

    const header = document.createElement("header");
    this.routeTitle.textContent = "New route";
    const done = document.createElement("button");
    done.type = "button";
    done.className = "route-done";
    done.textContent = "Done";
    done.addEventListener("click", () => this.endRoute());
    header.append(this.routeTitle, done);

    this.routeReadout.className = "route-readout";
    this.routeReadout.setAttribute("aria-live", "polite");
    this.routeReadout.value = "0.00 NM · 0.00 km";
    this.routeInstruction.className = "route-instruction";
    this.routeInstruction.textContent = "Tap the map to place the first waypoint.";

    const pointActions = document.createElement("div");
    pointActions.className = "route-actions route-point-actions";
    this.routeUndoButton = this.routePanelButton("Undo waypoint", () => this.routeEditor?.undo());
    this.routeClearButton = this.routePanelButton("Clear", () => this.routeEditor?.clear());
    pointActions.append(this.routeUndoButton, this.routeClearButton);

    const routeActions = document.createElement("div");
    routeActions.className = "route-actions route-primary-actions";
    this.routeSaveButton = this.routePanelButton("Save route", () => this.askForRouteName());
    this.routeShareButton = this.routePanelButton("Share route", () => this.showRouteShareLink());
    this.routeNavigateButton = this.routePanelButton("Navigate route", () => {
      this.routeNavigationChoice.hidden = !this.routeNavigationChoice.hidden;
    });
    routeActions.append(
      this.routeSaveButton,
      this.routeShareButton,
      this.routeNavigateButton,
    );

    this.routeShareBox.className = "route-share-box";
    this.routeShareBox.hidden = true;
    this.routeShareInput.type = "url";
    this.routeShareInput.readOnly = true;
    this.routeShareInput.setAttribute("aria-label", "Route sharing link");
    this.routeShareInput.addEventListener("focus", () => this.routeShareInput.select());
    const copy = this.routePanelButton("Copy link", () => void this.copyRouteShareLink(copy));
    this.routeShareBox.append(this.routeShareInput, copy);

    this.routeNavigationChoice.className = "route-navigation-choice";
    this.routeNavigationChoice.hidden = true;
    const directionLabel = document.createElement("strong");
    directionLabel.textContent = "Choose direction";
    const directionActions = document.createElement("div");
    directionActions.className = "route-actions";
    directionActions.append(
      this.routePanelButton("Start → end", () => this.startRouteNavigation(1)),
      this.routePanelButton("End → start", () => this.startRouteNavigation(-1)),
    );
    this.routeNavigationChoice.append(directionLabel, directionActions);

    this.routeEditorContent.className = "route-editor-content";
    this.routeEditorContent.append(
      this.routeReadout,
      this.routeInstruction,
      pointActions,
      routeActions,
      this.routeNavigationChoice,
      this.routeShareBox,
    );

    this.routeNavigationDisplay.className = "route-navigation-display";
    this.routeNavigationDisplay.hidden = true;
    this.routeNavigationLabel.textContent = "Bearing to next waypoint";
    this.routeNavigationBearing.className = "route-navigation-bearing";
    this.routeNavigationBearing.value = "—";
    this.routeNavigationBearing.setAttribute("aria-live", "polite");
    this.routeNavigationDetail.className = "route-navigation-detail";
    this.routeNavigationStatus.className = "route-navigation-status";
    this.routeNavigationStatus.textContent = "Waiting for a GPS position…";
    this.routeNavigationNextButton.type = "button";
    this.routeNavigationNextButton.textContent = "Next waypoint";
    this.routeNavigationNextButton.addEventListener("click", () => this.advanceRouteTarget());
    this.routeNavigationBackButton.type = "button";
    this.routeNavigationBackButton.textContent = "Back to route";
    this.routeNavigationBackButton.addEventListener("click", () => this.endRouteNavigation());
    this.routeNavigationDisplay.append(
      this.routeNavigationLabel,
      this.routeNavigationBearing,
      this.routeNavigationDetail,
      this.routeNavigationStatus,
      this.routeNavigationNextButton,
      this.routeNavigationBackButton,
    );

    this.routePanel.append(header, this.routeEditorContent, this.routeNavigationDisplay);
  }

  private buildRouteLogControl(): void {
    this.routeLogControl.className = "route-log-control";
    this.routeLogControl.hidden = true;
    this.routeLogControl.setAttribute("aria-label", "Active route log");
    const recording = document.createElement("span");
    recording.className = "route-log-recording-dot";
    recording.setAttribute("aria-hidden", "true");
    const label = document.createElement("strong");
    label.textContent = "Logging route";
    this.routeLogControlStatus.value = "Waiting for GPS";
    this.routeLogControlStatus.setAttribute("aria-live", "polite");
    this.routeLogMotorButton.type = "button";
    this.routeLogMotorButton.className = "route-log-motor-button";
    this.routeLogMotorButton.textContent = "On motor";
    this.routeLogMotorButton.title = "Flag when the engine is running";
    this.routeLogMotorButton.setAttribute("aria-pressed", "false");
    this.routeLogMotorButton.addEventListener("click", () => this.toggleRouteLogMotor());
    const stop = document.createElement("button");
    stop.type = "button";
    stop.textContent = "Stop";
    stop.addEventListener("click", () => void this.stopRouteLogging());
    this.routeLogControl.append(
      recording,
      label,
      this.routeLogControlStatus,
      this.routeLogMotorButton,
      stop,
    );
  }

  private buildRouteLogViewControl(): void {
    this.routeLogViewControl.className = "route-log-view-control";
    this.routeLogViewControl.hidden = true;
    this.routeLogViewControl.setAttribute("aria-label", "Displayed route log");
    const heading = document.createElement("div");
    const label = document.createElement("span");
    label.textContent = "Viewing route log";
    this.routeLogViewTitle.textContent = "—";
    this.routeLogViewDetail.className = "route-log-view-detail";
    heading.append(label, this.routeLogViewTitle, this.routeLogViewDetail);

    const legend = document.createElement("div");
    legend.className = "route-log-legend";
    const sailing = document.createElement("span");
    sailing.innerHTML = '<i data-mode="sailing" aria-hidden="true"></i>Motor off';
    const motor = document.createElement("span");
    motor.innerHTML = '<i data-mode="motor" aria-hidden="true"></i>On motor';
    legend.append(sailing, motor);

    const hide = document.createElement("button");
    hide.type = "button";
    hide.textContent = "Hide";
    hide.addEventListener("click", () => this.clearViewedRouteLog());
    this.routeLogViewControl.append(heading, hide, legend);
  }

  private routePanelButton(label: string, action: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.addEventListener("click", action);
    return button;
  }

  private buildRouteNameDialog(): void {
    this.routeNameDialog.className = "route-name-dialog";
    this.routeNameDialog.setAttribute("aria-labelledby", "route-name-title");
    const form = document.createElement("form");
    form.method = "dialog";
    const title = document.createElement("h2");
    title.id = "route-name-title";
    title.textContent = "Save route";
    const label = document.createElement("label");
    label.textContent = "Route name";
    this.routeNameInput.type = "text";
    this.routeNameInput.required = true;
    this.routeNameInput.maxLength = 80;
    this.routeNameInput.autocomplete = "off";
    label.append(this.routeNameInput);
    const actions = document.createElement("div");
    actions.className = "route-dialog-actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.routeNameDialog.close());
    const save = document.createElement("button");
    save.type = "submit";
    save.textContent = "Save";
    actions.append(cancel, save);
    form.append(title, label, actions);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.persistActiveRoute(this.routeNameInput.value);
    });
    this.routeNameDialog.append(form);
  }

  private startNewRoute(): void {
    this.openRoute({ name: "New route", points: [] }, null);
  }

  private openRoute(
    route: RouteDefinition,
    routeId: string | null,
    shared = false,
  ): void {
    if (!this.handle) return;
    this.root.classList.remove("is-man-overboard");
    this.hideSearchResults();
    this.routeEditor?.destroy();
    this.routeEditor = new RouteEditor(this.handle.map, (state) => this.updateRoute(state));
    this.activeRouteId = routeId;
    this.activeRouteName = route.name || "New route";
    this.routeTitle.textContent = shared ? `Shared: ${this.activeRouteName}` : this.activeRouteName;
    this.routeState = null;
    this.routeNavigating = false;
    this.routeTargetIndex = null;
    this.routeTargetNeedsSelection = false;
    this.manOverboardNavigation = false;
    this.routeEditorContent.hidden = false;
    this.routeNavigationDisplay.hidden = true;
    this.routeNavigationChoice.hidden = true;
    this.routeShareBox.hidden = true;
    this.routePanel.hidden = false;
    this.root.classList.add("has-route-panel");
    this.routeEditor.start(route.points);
    if (route.points.length >= 2) this.fitRoute(route.points);
  }

  private fitRoute(points: RoutePoint[]): void {
    if (!this.handle || points.length < 2) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const point of points) bounds.extend(point);
    this.handle.map.fitBounds(bounds, {
      padding: { top: 80, right: 80, bottom: 260, left: 80 },
      maxZoom: 14,
      duration: 550,
    });
  }

  private updateRoute(state: RouteEditorState): void {
    this.routeState = state;
    this.routeReadout.value =
      `${state.nauticalMiles.toFixed(2)} NM · ${state.kilometres.toFixed(2)} km`;
    this.routeInstruction.textContent =
      state.pointCount === 0
        ? "Tap the map to place the first waypoint."
        : state.pointCount === 1
          ? "Tap again to continue the route. Drag the waypoint to adjust it."
          : `${state.pointCount} waypoints. Tap to continue or drag any waypoint to adjust.`;
    const empty = state.pointCount === 0;
    const incomplete = state.pointCount < 2;
    if (this.routeUndoButton) this.routeUndoButton.disabled = empty;
    if (this.routeClearButton) this.routeClearButton.disabled = empty;
    if (this.routeSaveButton) this.routeSaveButton.disabled = incomplete;
    if (this.routeShareButton) this.routeShareButton.disabled = incomplete;
    if (this.routeNavigateButton) this.routeNavigateButton.disabled = incomplete;
    this.routeNavigationChoice.hidden = true;
    this.routeShareBox.hidden = true;
  }

  private askForRouteName(): void {
    if (!this.routeState || this.routeState.pointCount < 2) return;
    this.routeNameInput.value =
      this.activeRouteId === null && this.activeRouteName === "New route"
        ? ""
        : this.activeRouteName;
    this.routeNameDialog.showModal();
    requestAnimationFrame(() => this.routeNameInput.focus());
  }

  private persistActiveRoute(name: string): void {
    if (!this.routeState || this.routeState.pointCount < 2 || !name.trim()) return;
    try {
      const route = saveRoute(
        { name: name.trim(), points: this.routeState.points },
        this.activeRouteId,
      );
      this.activeRouteId = route.id;
      this.activeRouteName = route.name;
      this.routeTitle.textContent = route.name;
      this.routeInstruction.textContent = `Saved locally as “${route.name}”.`;
      this.routeNameDialog.close();
      this.renderSavedRoutes();
    } catch (error) {
      this.routeInstruction.textContent =
        error instanceof Error ? error.message : "The route could not be saved.";
    }
  }

  private showRouteShareLink(): void {
    if (!this.routeState || this.routeState.pointCount < 2) return;
    try {
      this.routeShareInput.value = routeUrl({
        name:
          this.activeRouteId === null && this.activeRouteName === "New route"
            ? "Shared route"
            : this.activeRouteName,
        points: this.routeState.points,
      });
      this.routeShareBox.hidden = false;
      this.routeShareInput.focus();
    } catch (error) {
      this.routeInstruction.textContent =
        error instanceof Error ? error.message : "The route link could not be created.";
    }
  }

  private async copyRouteShareLink(button: HTMLButtonElement): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.routeShareInput.value);
      button.textContent = "Copied";
    } catch {
      this.routeShareInput.select();
      button.textContent = document.execCommand("copy") ? "Copied" : "Select and copy";
    }
    window.setTimeout(() => {
      if (!this.stopped) button.textContent = "Copy link";
    }, 1600);
  }

  private startRouteNavigation(step: 1 | -1): void {
    if (!this.routeState || this.routeState.pointCount < 2 || !this.routeEditor) return;
    this.manOverboardNavigation = false;
    this.routeNavigating = true;
    this.routeDirectionStep = step;
    this.routeTargetIndex = step === 1 ? 0 : this.routeState.pointCount - 1;
    this.routeTargetNeedsSelection = true;
    this.routeEditor.setEditing(false);
    this.routeEditor.setTarget(this.routeTargetIndex);
    this.routeEditorContent.hidden = true;
    this.routeNavigationDisplay.hidden = false;
    this.routeTitle.textContent = `Navigating: ${this.activeRouteName}`;
    this.routeNavigationLabel.textContent = "Bearing to next waypoint";
    this.routeNavigationBackButton.textContent = "Back to route";
    this.routeNavigationBearing.value = "—";
    this.routeNavigationDetail.textContent =
      `Next: waypoint ${this.routeTargetIndex + 1} of ${this.routeState.pointCount}`;
    this.routeNavigationStatus.textContent = "Waiting for a GPS position…";
    this.routeNavigationNextButton.hidden = false;
    this.updateRouteNavigationNextButton();
    this.root.classList.add("is-navigating-route");
    if (this.lastTrackingStatus.state !== "tracking" && this.lastTrackingStatus.state !== "searching") {
      this.tracking?.start();
    } else {
      this.updateRouteNavigation(this.lastTrackingStatus);
    }
    this.updateCollisionWarning();
  }

  private updateRouteNavigation(status: TrackingStatus): void {
    if (this.routeNavigating && (status.longitude === undefined || status.latitude === undefined)) {
      this.routeNavigationStatus.textContent =
        status.message ??
        (status.state === "searching"
          ? "Waiting for a GPS position…"
          : status.state === "off"
            ? "Position tracking is off. Use the position button to resume."
            : "Waiting for position…");
    }
    if (
      !this.routeNavigating ||
      !this.routeState ||
      this.routeTargetIndex === null ||
      status.longitude === undefined ||
      status.latitude === undefined
    ) {
      return;
    }
    const current: RoutePoint = [status.longitude, status.latitude];
    if (this.routeTargetNeedsSelection && !this.manOverboardNavigation) {
      this.routeTargetIndex = this.initialRouteTarget(current);
      this.routeTargetNeedsSelection = false;
    }
    let targetIndex = this.routeTargetIndex;
    let target = this.routeState.points[targetIndex];
    let distanceKm = pointDistanceKm(current, target);
    if (this.manOverboardNavigation) {
      const bearing = Math.round(bearingToPoint(current, target)) % 360;
      this.routeNavigationBearing.value =
        distanceKm <= ROUTE_ARRIVAL_KM
          ? "At position"
          : `${String(bearing).padStart(3, "0")}°T`;
      this.routeNavigationDetail.textContent =
        `Man overboard · ${kilometresToNauticalMiles(distanceKm).toFixed(2)} NM`;
      this.routeNavigationStatus.textContent = status.accuracy
        ? `GPS accuracy ±${Math.round(status.accuracy)} m`
        : "Following current GPS position";
      this.routeEditor?.setTarget(targetIndex);
      this.routeEditor?.setManOverboard(targetIndex);
      return;
    }
    while (distanceKm <= ROUTE_ARRIVAL_KM) {
      const next: number = targetIndex + this.routeDirectionStep;
      if (next < 0 || next >= this.routeState.pointCount) {
        this.routeNavigationBearing.value = "Arrived";
        this.routeNavigationDetail.textContent = "Route complete";
        this.routeNavigationStatus.textContent = `At waypoint ${targetIndex + 1}.`;
        this.routeEditor?.setTarget(targetIndex);
        this.updateRouteNavigationNextButton();
        return;
      }
      targetIndex = next;
      this.routeTargetIndex = targetIndex;
      target = this.routeState.points[targetIndex];
      distanceKm = pointDistanceKm(current, target);
    }
    const bearing = Math.round(bearingToPoint(current, target)) % 360;
    this.routeNavigationBearing.value = `${String(bearing).padStart(3, "0")}°T`;
    this.routeNavigationDetail.textContent =
      `Waypoint ${targetIndex + 1} of ${this.routeState.pointCount} · ${kilometresToNauticalMiles(distanceKm).toFixed(2)} NM`;
    this.routeNavigationStatus.textContent = status.accuracy
      ? `GPS accuracy ±${Math.round(status.accuracy)} m`
      : "Following current GPS position";
    this.routeEditor?.setTarget(targetIndex);
    this.updateRouteNavigationNextButton();
  }

  private initialRouteTarget(current: RoutePoint): number {
    if (!this.routeState?.points.length) return 0;
    const points = this.routeState.points;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < points.length; index++) {
      const distance = pointDistanceKm(current, points[index]);
      if (distance < closestDistance) {
        closestIndex = index;
        closestDistance = distance;
      }
    }
    const nextIndex = closestIndex + this.routeDirectionStep;
    if (nextIndex < 0 || nextIndex >= points.length) return closestIndex;
    const previousIndex = closestIndex - this.routeDirectionStep;
    const waypoint = points[closestIndex];
    const next = points[nextIndex];
    const latitudeScale = Math.cos((waypoint[1] * Math.PI) / 180);
    const relative = [
      (current[0] - waypoint[0]) * latitudeScale,
      current[1] - waypoint[1],
    ];
    const outgoing = [
      (next[0] - waypoint[0]) * latitudeScale,
      next[1] - waypoint[1],
    ];
    const outgoingLengthSquared = outgoing[0] ** 2 + outgoing[1] ** 2;
    if (!outgoingLengthSquared) return closestIndex;
    const outgoingProgress =
      (relative[0] * outgoing[0] + relative[1] * outgoing[1]) / outgoingLengthSquared;
    if (outgoingProgress <= 0) return closestIndex;
    if (previousIndex < 0 || previousIndex >= points.length) return nextIndex;

    const segmentDistanceSquared = (from: RoutePoint, to: RoutePoint): number => {
      const segment = [
        (to[0] - from[0]) * latitudeScale,
        to[1] - from[1],
      ];
      const offset = [
        (current[0] - from[0]) * latitudeScale,
        current[1] - from[1],
      ];
      const lengthSquared = segment[0] ** 2 + segment[1] ** 2;
      if (!lengthSquared) return offset[0] ** 2 + offset[1] ** 2;
      const progress = Math.max(
        0,
        Math.min(1, (offset[0] * segment[0] + offset[1] * segment[1]) / lengthSquared),
      );
      const x = offset[0] - segment[0] * progress;
      const y = offset[1] - segment[1] * progress;
      return x ** 2 + y ** 2;
    };
    const outgoingDistance = segmentDistanceSquared(waypoint, next);
    const incomingDistance = segmentDistanceSquared(points[previousIndex], waypoint);
    return outgoingDistance < incomingDistance ? nextIndex : closestIndex;
  }

  private advanceRouteTarget(): void {
    if (
      !this.routeNavigating ||
      this.manOverboardNavigation ||
      !this.routeState ||
      this.routeTargetIndex === null
    ) return;
    const nextIndex = this.routeTargetIndex + this.routeDirectionStep;
    if (nextIndex < 0 || nextIndex >= this.routeState.pointCount) return;
    this.routeTargetNeedsSelection = false;
    this.routeTargetIndex = nextIndex;
    this.routeEditor?.setTarget(nextIndex);
    this.updateRouteNavigationNextButton();
    this.updateRouteNavigation(this.lastTrackingStatus);
    this.updateCollisionWarning();
  }

  private updateRouteNavigationNextButton(): void {
    const nextIndex = this.routeTargetIndex === null
      ? -1
      : this.routeTargetIndex + this.routeDirectionStep;
    this.routeNavigationNextButton.disabled =
      this.manOverboardNavigation ||
      !this.routeState ||
      nextIndex < 0 ||
      nextIndex >= this.routeState.pointCount;
  }

  private endRouteNavigation(): void {
    if (!this.routeEditor) return;
    if (this.manOverboardNavigation || (this.routeState?.pointCount ?? 0) < 2) {
      this.endRoute();
      return;
    }
    this.routeNavigating = false;
    this.routeTargetIndex = null;
    this.routeTargetNeedsSelection = false;
    this.routeEditor.setTarget(null);
    this.routeEditor.setEditing(true);
    this.routeEditorContent.hidden = false;
    this.routeNavigationDisplay.hidden = true;
    this.routeNavigationChoice.hidden = true;
    this.routeTitle.textContent = this.activeRouteName;
    this.root.classList.remove("is-navigating-route");
    this.updateCollisionWarning();
  }

  private endRoute(restoreFocus = true): void {
    this.routeNavigating = false;
    this.routeTargetIndex = null;
    this.routeTargetNeedsSelection = false;
    this.manOverboardNavigation = false;
    this.routeEditor?.destroy();
    this.routeEditor = null;
    this.routeState = null;
    this.routePanel.hidden = true;
    this.root.classList.remove("has-route-panel", "is-navigating-route", "is-man-overboard");
    this.updateCollisionWarning();
    if (restoreFocus && !this.stopped) {
      this.root.querySelector<HTMLButtonElement>(".map-menu-button")?.focus();
    }
  }

  private async startRouteLogging(): Promise<void> {
    if (this.activeRouteLog || !this.routeLogButton || !this.routeLogOverlay) return;
    this.clearViewedRouteLog();
    this.routeLogButton.disabled = true;
    this.routeLogButton.textContent = "Starting route log…";
    try {
      const log = await startRouteLog();
      if (this.stopped) {
        await finishRouteLog(log.id);
        return;
      }
      this.activeRouteLog = log;
      this.routeLogLastTimestamp = 0;
      this.routeLogPreviousFix = null;
      this.routeLogWindReadings = [];
      this.routeLogWindLabel = "wind pending";
      this.setRouteLogMotorUi(false);
      this.routeLogOverlay.start();
      this.routeLogControl.hidden = false;
      this.routeLogControlStatus.value = "Waiting for GPS · wind pending";
      this.routeLogButton.textContent = "Stop route log";
      this.routeLogButton.disabled = false;
      this.routeLogMenuStatus.textContent =
        "Recording position, device speed, time, and interpolated SMHI wind.";
      this.routeLogMenuStatus.dataset.tone = "active";
      void this.refreshRouteLogWind(false);
      if (this.lastTrackingStatus.state === "tracking") {
        this.recordRouteLogPosition(this.lastTrackingStatus);
      } else if (this.lastTrackingStatus.state !== "searching") {
        this.tracking?.start();
      }
    } catch (error) {
      this.routeLogButton.textContent = "Log route";
      this.routeLogButton.disabled = false;
      this.routeLogMenuStatus.textContent =
        error instanceof Error ? error.message : "The route log could not be started.";
      this.routeLogMenuStatus.dataset.tone = "error";
    }
  }

  private async stopRouteLogging(): Promise<void> {
    const log = this.activeRouteLog;
    if (!log) return;
    this.activeRouteLog = null;
    this.stopRouteLogWind();
    this.routeLogOverlay?.clear();
    this.routeLogControl.hidden = true;
    this.setRouteLogMotorUi(false);
    if (this.routeLogButton) {
      this.routeLogButton.disabled = true;
      this.routeLogButton.textContent = "Saving route log…";
    }
    try {
      await this.routeLogWrite;
      const finished = await finishRouteLog(log.id);
      const count = finished?.sampleCount ?? log.sampleCount;
      this.routeLogMenuStatus.textContent =
        `Saved ${count} position${count === 1 ? "" : "s"} locally for future performance analysis.`;
      delete this.routeLogMenuStatus.dataset.tone;
    } catch (error) {
      this.routeLogMenuStatus.textContent =
        error instanceof Error ? error.message : "The route log could not be finished.";
      this.routeLogMenuStatus.dataset.tone = "error";
    } finally {
      if (this.routeLogButton) {
        this.routeLogButton.textContent = "Log route";
        this.routeLogButton.disabled = false;
      }
      void this.updateRouteLogHistory();
    }
  }

  private finishRouteLogOnShutdown(): void {
    const log = this.activeRouteLog;
    if (!log) return;
    this.activeRouteLog = null;
    this.stopRouteLogWind();
    this.routeLogOverlay?.clear();
    this.setRouteLogMotorUi(false);
    void this.routeLogWrite
      .then(
        () => finishRouteLog(log.id),
        () => finishRouteLog(log.id),
      )
      .catch((error: unknown) => console.warn("klint: could not finalize route log:", error));
  }

  private stopRouteLogWind(): void {
    this.routeLogWindAbort?.abort();
    this.routeLogWindAbort = null;
    if (this.routeLogWindTimer !== null) window.clearTimeout(this.routeLogWindTimer);
    this.routeLogWindTimer = null;
    this.routeLogWindReadings = [];
  }

  private setRouteLogMotorUi(motorOn: boolean): void {
    this.routeLogMotorOn = motorOn;
    this.routeLogMotorButton.setAttribute("aria-pressed", String(motorOn));
    this.routeLogMotorButton.title = motorOn
      ? "Engine use is being logged. Press to mark the engine off."
      : "Flag when the engine is running";
  }

  private toggleRouteLogMotor(): void {
    const log = this.activeRouteLog;
    if (!log) return;
    const motorOn = !this.routeLogMotorOn;
    const recordedAt = Date.now();
    this.setRouteLogMotorUi(motorOn);
    this.routeLogWrite = this.routeLogWrite
      .then(() => appendRouteLogMotorEvent(log.id, motorOn, recordedAt))
      .catch((error: unknown) => this.failRouteLogging(log, error));
  }

  private scheduleRouteLogWindRefresh(): void {
    if (!this.activeRouteLog || this.stopped) return;
    if (this.routeLogWindTimer !== null) window.clearTimeout(this.routeLogWindTimer);
    this.routeLogWindTimer = window.setTimeout(
      () => void this.refreshRouteLogWind(true),
      LIVE_WIND_REFRESH_MS,
    );
  }

  private setRouteLogWindLabel(label: string): void {
    this.routeLogWindLabel = label;
    this.routeLogControlStatus.value = this.routeLogControlStatus.value.replace(
      /wind (?:pending|ready|unavailable)/,
      label,
    );
  }

  private async refreshRouteLogWind(force: boolean): Promise<void> {
    if (!this.activeRouteLog) return;
    if (!force && this.windReadings.length) {
      this.routeLogWindReadings = this.windReadings;
      this.setRouteLogWindLabel("wind ready");
      this.scheduleRouteLogWindRefresh();
      return;
    }
    this.routeLogWindAbort?.abort();
    const controller = new AbortController();
    this.routeLogWindAbort = controller;
    try {
      this.routeLogWindReadings = await fetchLatestWindReadings(controller.signal);
      if (!this.activeRouteLog || controller.signal.aborted) return;
      this.setRouteLogWindLabel("wind ready");
    } catch {
      if (controller.signal.aborted || !this.activeRouteLog) return;
      this.setRouteLogWindLabel("wind unavailable");
    } finally {
      if (this.routeLogWindAbort === controller) this.routeLogWindAbort = null;
      this.scheduleRouteLogWindRefresh();
    }
  }

  private loggedWindAt(longitude: number, latitude: number): LoggedWind | null {
    const wind = interpolateWind(this.routeLogWindReadings, { longitude, latitude });
    if (!wind || wind.nearestDistanceKm > MAX_LIVE_STATION_DISTANCE_KM) return null;
    return {
      speedKnots: wind.speedKnots,
      direction: wind.direction,
      observedAt: wind.observedAt,
      nearestStation: wind.nearestStation,
      nearestDistanceKm: wind.nearestDistanceKm,
      stationCount: wind.stationCount,
      source: "SMHI-interpolated",
    };
  }

  private recordRouteLogPosition(status: TrackingStatus): void {
    const log = this.activeRouteLog;
    if (
      !log ||
      status.longitude === undefined ||
      status.latitude === undefined
    ) {
      return;
    }
    const recordedAt = status.timestamp ?? Date.now();
    if (recordedAt <= this.routeLogLastTimestamp) return;
    this.routeLogLastTimestamp = recordedAt;
    const point: RoutePoint = [status.longitude, status.latitude];
    const rawSpeed = status.speedMetresPerSecond;
    const elapsedSeconds = this.routeLogPreviousFix
      ? (recordedAt - this.routeLogPreviousFix.recordedAt) / 1000
      : 0;
    const calculatedSpeed =
      rawSpeed === undefined && this.routeLogPreviousFix && elapsedSeconds >= 1
        ? (pointDistanceKm(this.routeLogPreviousFix.point, point) * 1000) / elapsedSeconds
        : null;
    const speedMetresPerSecond = rawSpeed ?? calculatedSpeed;
    const speedSource: RouteLogSample["speedSource"] =
      rawSpeed !== undefined ? "device" : calculatedSpeed !== null ? "calculated" : "unavailable";
    this.routeLogPreviousFix = { point, recordedAt };
    const sample = {
      recordedAt,
      longitude: status.longitude,
      latitude: status.latitude,
      accuracyMetres: status.accuracy ?? null,
      speedMetresPerSecond,
      speedSource,
      motorOn: this.routeLogMotorOn,
      wind: this.loggedWindAt(status.longitude, status.latitude),
    };
    this.routeLogOverlay?.add(point, this.routeLogMotorOn);
    this.routeLogControlStatus.value = `Saving fix · ${this.routeLogWindLabel}`;
    this.routeLogWrite = this.routeLogWrite
      .then(async () => {
        const count = await appendRouteLogSample(log.id, sample);
        if (this.activeRouteLog?.id !== log.id) return;
        this.activeRouteLog = { ...this.activeRouteLog, sampleCount: count };
        const speedLabel = speedMetresPerSecond === null
          ? "speed unavailable"
          : `${(speedMetresPerSecond * 1.943_844).toFixed(1)} kn${speedSource === "calculated" ? " estimated" : ""}`;
        this.routeLogControlStatus.value =
          `${count} fix${count === 1 ? "" : "es"} · ${speedLabel} · ${this.routeLogWindLabel}`;
      })
      .catch((error: unknown) => this.failRouteLogging(log, error));
  }

  private failRouteLogging(log: RouteLog, error: unknown): void {
    if (this.activeRouteLog?.id !== log.id) return;
    this.activeRouteLog = null;
    this.stopRouteLogWind();
    this.routeLogOverlay?.clear();
    this.setRouteLogMotorUi(false);
    this.routeLogControl.hidden = true;
    if (this.routeLogButton) {
      this.routeLogButton.textContent = "Log route";
      this.routeLogButton.disabled = false;
    }
    const message = error instanceof Error ? error.message : "Local log storage failed.";
    this.routeLogMenuStatus.textContent = `Route logging stopped: ${message}`;
    this.routeLogMenuStatus.dataset.tone = "error";
    this.banner.textContent = `Route logging stopped: ${message}`;
    this.banner.hidden = false;
    void finishRouteLog(log.id);
  }

  private async updateRouteLogHistory(): Promise<void> {
    if (this.activeRouteLog) return;
    try {
      const logs = await loadRouteLogs();
      const fixes = logs.reduce((total, log) => total + log.sampleCount, 0);
      this.routeLogMenuStatus.textContent = logs.length
        ? `${logs.length} log${logs.length === 1 ? "" : "s"} · ${fixes} stored GPS fix${fixes === 1 ? "" : "es"} on this device.`
        : "Logs are stored locally for future boat-performance analysis.";
      delete this.routeLogMenuStatus.dataset.tone;
    } catch {
      this.routeLogMenuStatus.textContent = "Stored route logs could not be read.";
      this.routeLogMenuStatus.dataset.tone = "error";
    }
  }

  private cancelMapLongPress(): void {
    if (this.mapLongPressTimer !== null) window.clearTimeout(this.mapLongPressTimer);
    this.mapLongPressTimer = null;
    this.mapLongPressStart = null;
  }

  private startWaypointNavigation(point: RoutePoint): void {
    if (!this.handle) return;
    this.setMenuOpen(false);
    this.openRoute({ name: "Waypoint", points: [point] }, null);
    if (!this.routeEditor || !this.routeState) return;
    this.routeNavigating = true;
    this.routeDirectionStep = 1;
    this.routeTargetIndex = 0;
    this.routeTargetNeedsSelection = false;
    this.routeEditor.setEditing(false);
    this.routeEditor.setTarget(0);
    this.routeEditorContent.hidden = true;
    this.routeNavigationDisplay.hidden = false;
    this.routeTitle.textContent = "Navigating: Waypoint";
    this.routeNavigationLabel.textContent = "Bearing to waypoint";
    this.routeNavigationBackButton.textContent = "End navigation";
    this.routeNavigationNextButton.hidden = true;
    this.routeNavigationBearing.value = "—";
    this.routeNavigationDetail.textContent = "Waypoint set from map";
    this.routeNavigationStatus.textContent = "Waiting for a GPS position…";
    this.root.classList.add("is-navigating-route");
    if (this.lastTrackingStatus.state !== "tracking" && this.lastTrackingStatus.state !== "searching") {
      this.tracking?.start();
    } else {
      this.updateRouteNavigation(this.lastTrackingStatus);
    }
    this.updateCollisionWarning();
  }

  private requestManOverboard(): void {
    if (this.pendingManOverboard) return;
    const status = this.lastTrackingStatus;
    const timestamp = status.timestamp ?? 0;
    if (
      status.state === "tracking" &&
      status.longitude !== undefined &&
      status.latitude !== undefined &&
      Date.now() - timestamp <= FRESH_POSITION_MS
    ) {
      this.captureManOverboard(status);
      return;
    }
    this.pendingManOverboard = true;
    this.manOverboardTimer = window.setTimeout(() => {
      this.cancelPendingManOverboard("No current GPS position was received. Try again with a clear view of the sky.");
    }, 30_000);
    if (this.manOverboardButton) {
      this.manOverboardButton.textContent = "Waiting for GPS position…";
      this.manOverboardButton.disabled = true;
    }
    this.banner.textContent = "Man overboard selected — waiting for a current GPS position.";
    this.banner.hidden = false;
    if (status.state !== "searching" && status.state !== "tracking") this.tracking?.start();
  }

  private captureManOverboard(status: TrackingStatus): void {
    if (status.longitude === undefined || status.latitude === undefined || !this.handle) return;
    this.pendingManOverboard = false;
    if (this.manOverboardTimer !== null) window.clearTimeout(this.manOverboardTimer);
    this.manOverboardTimer = null;
    if (this.manOverboardButton) {
      this.manOverboardButton.textContent = "Man overboard";
      this.manOverboardButton.disabled = false;
    }
    const recordedAt = status.timestamp ?? Date.now();
    void recordManOverboard({
      recordedAt,
      longitude: status.longitude,
      latitude: status.latitude,
      accuracyMetres: status.accuracy ?? null,
    }).catch((error: unknown) => {
      console.warn("klint: could not persist man-overboard position:", error);
    });

    this.openRoute(
      { name: "Man overboard", points: [[status.longitude, status.latitude]] },
      null,
    );
    if (!this.routeEditor || !this.routeState) return;
    this.manOverboardNavigation = true;
    this.routeNavigating = true;
    this.routeTargetIndex = 0;
    this.routeTargetNeedsSelection = false;
    this.routeEditor.setEditing(false);
    this.routeEditor.setTarget(0);
    this.routeEditor.setManOverboard(0);
    this.routeEditorContent.hidden = true;
    this.routeNavigationDisplay.hidden = false;
    this.routeTitle.textContent = "MAN OVERBOARD";
    this.routeNavigationLabel.textContent = "Bearing to man overboard";
    this.routeNavigationBackButton.textContent = "End MOB navigation";
    this.routeNavigationNextButton.hidden = true;
    this.routeNavigationBearing.value = "—";
    this.routeNavigationDetail.textContent = "Man overboard position recorded";
    this.root.classList.add("is-navigating-route", "is-man-overboard");
    this.updateRouteNavigation(status);
    this.updateCollisionWarning();
  }

  private cancelPendingManOverboard(message: string): void {
    if (!this.pendingManOverboard) return;
    this.pendingManOverboard = false;
    if (this.manOverboardTimer !== null) window.clearTimeout(this.manOverboardTimer);
    this.manOverboardTimer = null;
    if (this.manOverboardButton) {
      this.manOverboardButton.textContent = "Man overboard";
      this.manOverboardButton.disabled = false;
    }
    this.banner.textContent = message;
    this.banner.hidden = false;
  }

  private buildMenu(menuButton: HTMLButtonElement): void {
    this.backdrop.className = "map-menu-backdrop";
    this.backdrop.type = "button";
    this.backdrop.hidden = true;
    this.backdrop.setAttribute("aria-label", "Close menu");
    this.backdrop.addEventListener("click", () => this.setMenuOpen(false));

    this.menu.className = "map-menu";
    this.menu.hidden = true;
    this.menu.setAttribute("role", "dialog");
    this.menu.setAttribute("aria-modal", "true");
    this.menu.setAttribute("aria-label", "Map menu");

    this.mainPanel.className = "map-menu-panel";
    this.mainPanel.append(this.menuHeader("Klint"), this.buildSearch());

    const actions = document.createElement("nav");
    actions.className = "map-menu-actions";
    actions.setAttribute("aria-label", "Main menu");
    this.installButton = this.menuAction(
      "Install Klint",
      () => void this.installApp(),
      false,
      false,
    );
    this.manOverboardButton = this.menuAction(
      "Man overboard",
      () => this.requestManOverboard(),
      true,
    );
    this.manOverboardButton.classList.add("map-menu-action-danger");
    actions.append(
      this.manOverboardButton,
      this.submenuAction("Maps", "maps"),
      this.submenuAction("Routes", "routes"),
      this.submenuAction("Logs", "routeLogs"),
      this.submenuAction("Wind", "wind"),
      this.submenuAction("AIS traffic", "ais"),
      this.submenuAction("Settings", "settings"),
      this.installButton,
    );
    this.installNote.className = "map-menu-note map-install-note";
    this.installNote.hidden = true;
    this.mainPanel.append(actions, this.installNote);

    this.mapsPanel.className = "map-menu-panel";
    this.mapsPanel.hidden = true;
    this.mapsPanel.append(this.menuHeader("Maps", () => this.showMenuPanel("main"), "main menu"));
    const mapActions = document.createElement("nav");
    mapActions.className = "map-menu-actions";
    mapActions.setAttribute("aria-label", "Map actions");
    mapActions.append(
      this.menuAction("Select layers", () => this.showMenuPanel("layers"), false, false),
      this.menuAction(
        "Download or remove maps",
        () => this.options.onManageCharts(),
        !this.options.hasCatalogue,
      ),
      this.menuAction(
        "Share a map",
        () => this.options.onShareCharts(),
        !this.options.router.packCount(),
      ),
    );
    this.mapsPanel.append(mapActions);

    this.layersPanel.className = "map-menu-panel";
    this.layersPanel.hidden = true;
    const layerHeader = this.menuHeader(
      "Select layers",
      () => this.showMenuPanel("maps"),
      "maps",
    );
    this.layerList.className = "map-layer-list";
    const loading = document.createElement("p");
    loading.className = "map-menu-note";
    loading.textContent = "Loading stored maps…";
    this.layerList.append(loading);
    this.layersPanel.append(layerHeader, this.layerList);

    this.routesPanel.className = "map-menu-panel";
    this.routesPanel.hidden = true;
    this.routesPanel.append(
      this.menuHeader("Routes", () => this.showMenuPanel("main"), "main menu"),
    );
    const routeActions = document.createElement("nav");
    routeActions.className = "map-menu-actions";
    routeActions.setAttribute("aria-label", "Route actions");
    this.newRouteButton = this.menuAction("New route", () => this.startNewRoute(), true);
    routeActions.append(
      this.newRouteButton,
      this.menuAction(
        "Show saved",
        () => this.showMenuPanel("savedRoutes"),
        false,
        false,
      ),
    );
    this.routesPanel.append(routeActions);

    this.savedRoutesPanel.className = "map-menu-panel";
    this.savedRoutesPanel.hidden = true;
    this.savedRoutesPanel.append(
      this.menuHeader("Saved routes", () => this.showMenuPanel("routes"), "routes"),
    );
    this.savedRouteList.className = "saved-route-list";
    this.savedRoutesPanel.append(this.savedRouteList);
    this.renderSavedRoutes();

    this.routeLogsPanel.className = "map-menu-panel";
    this.routeLogsPanel.hidden = true;
    this.routeLogsPanel.append(
      this.menuHeader("Logs", () => this.showMenuPanel("main"), "main menu"),
    );
    const logActions = document.createElement("nav");
    logActions.className = "map-menu-actions";
    logActions.setAttribute("aria-label", "Log actions");
    this.routeLogButton = this.menuAction(
      "Log route",
      () => {
        if (this.activeRouteLog) void this.stopRouteLogging();
        else void this.startRouteLogging();
      },
      true,
    );
    logActions.append(this.routeLogButton);
    this.routeLogMenuStatus.className = "map-menu-note route-log-menu-status";
    this.routeLogMenuStatus.textContent =
      "Logs are stored locally for future boat-performance analysis.";
    this.routeLogList.className = "route-log-list";
    this.routeLogsPanel.append(logActions, this.routeLogMenuStatus, this.routeLogList);
    void this.updateRouteLogHistory();

    this.windPanel.className = "map-menu-panel";
    this.windPanel.hidden = true;
    this.windPanel.append(
      this.menuHeader("Wind", () => this.showMenuPanel("main"), "main menu"),
      this.buildWindSettings(),
    );

    this.aisPanel.className = "map-menu-panel";
    this.aisPanel.hidden = true;
    this.aisPanel.append(
      this.menuHeader("AIS traffic", () => this.showMenuPanel("main"), "main menu"),
      this.buildAisSettings(),
    );

    this.settingsPanel.className = "map-menu-panel";
    this.settingsPanel.hidden = true;
    this.settingsPanel.append(
      this.menuHeader("Settings", () => this.showMenuPanel("main"), "main menu"),
      this.buildSettings(),
    );

    this.menu.append(
      this.mainPanel,
      this.mapsPanel,
      this.layersPanel,
      this.routesPanel,
      this.savedRoutesPanel,
      this.routeLogsPanel,
      this.windPanel,
      this.aisPanel,
      this.settingsPanel,
    );
    this.unsubscribeInstallState = subscribeInstallState((state) =>
      this.updateInstallButton(state),
    );
    this.menu.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.setMenuOpen(false);
        menuButton.focus();
      }
    });
  }

  private menuHeader(
    titleText: string,
    back?: () => void,
    backTarget = "main menu",
  ): HTMLElement {
    const header = document.createElement("header");
    const titleGroup = document.createElement("div");
    titleGroup.className = "map-menu-title-group";
    if (back) {
      const backButton = document.createElement("button");
      backButton.className = "map-menu-back";
      backButton.type = "button";
      backButton.setAttribute("aria-label", `Back to ${backTarget}`);
      backButton.textContent = "‹";
      backButton.addEventListener("click", back);
      titleGroup.append(backButton);
    }
    const title = document.createElement("h2");
    title.textContent = titleText;
    titleGroup.append(title);

    const close = document.createElement("button");
    close.className = "map-menu-close";
    close.type = "button";
    close.setAttribute("aria-label", "Close menu");
    close.textContent = "×";
    close.addEventListener("click", () => this.setMenuOpen(false));
    header.append(titleGroup, close);
    return header;
  }

  private submenuAction(label: string, panel: MenuPanel): HTMLButtonElement {
    const button = this.menuAction(label, () => this.showMenuPanel(panel), false, false);
    button.classList.add("map-menu-action-submenu");
    const arrow = document.createElement("span");
    arrow.setAttribute("aria-hidden", "true");
    arrow.textContent = "›";
    button.append(arrow);
    return button;
  }

  private menuAction(
    label: string,
    action: () => void,
    disabled = false,
    closeMenu = true,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "map-menu-action";
    button.type = "button";
    button.textContent = label;
    button.disabled = disabled;
    button.addEventListener("click", () => {
      if (closeMenu) this.setMenuOpen(false);
      action();
    });
    return button;
  }

  private buildSettings(): HTMLElement {
    const preferences = uiPreferences();
    const settings = document.createElement("div");
    settings.className = "map-ui-settings";

    const sizeSetting = document.createElement("fieldset");
    sizeSetting.className = "map-ui-setting";
    const sizeLegend = document.createElement("legend");
    sizeLegend.textContent = "UI text size";
    const sizeOptions = document.createElement("div");
    sizeOptions.className = "map-ui-size-options";
    const sizes: Array<[UiTextSize, string]> = [
      ["small", "Small"],
      ["default", "Default"],
      ["large", "Large"],
    ];
    for (const [value, label] of sizes) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.textSize = value;
      button.textContent = label;
      button.setAttribute("aria-pressed", String(value === preferences.textSize));
      button.addEventListener("click", () => {
        setUiTextSize(value);
        for (const candidate of sizeOptions.querySelectorAll<HTMLButtonElement>("button")) {
          candidate.setAttribute("aria-pressed", String(candidate.dataset.textSize === value));
        }
      });
      sizeOptions.append(button);
    }
    sizeSetting.append(sizeLegend, sizeOptions);

    const darkSetting = document.createElement("label");
    darkSetting.className = "map-ui-setting map-ui-toggle";
    const darkText = document.createElement("span");
    const darkLabel = document.createElement("strong");
    darkLabel.textContent = "Dark mode";
    const darkDetail = document.createElement("small");
    darkDetail.textContent = "Use dark menus and controls";
    darkText.append(darkLabel, darkDetail);
    const darkMode = document.createElement("input");
    darkMode.type = "checkbox";
    darkMode.role = "switch";
    darkMode.checked = preferences.darkMode;
    darkMode.addEventListener("change", () => setDarkMode(darkMode.checked));
    darkSetting.append(darkText, darkMode);

    const windUnitSetting = document.createElement("fieldset");
    windUnitSetting.className = "map-ui-setting";
    const windUnitLegend = document.createElement("legend");
    windUnitLegend.textContent = "Wind speed";
    const windUnitDetail = document.createElement("small");
    windUnitDetail.textContent = "Unit used in the wind overlay";
    const windUnitOptions = document.createElement("div");
    windUnitOptions.className = "map-ui-size-options map-ui-unit-options";
    const units: Array<[WindSpeedUnit, string]> = [
      ["kn", "kn"],
      ["ms", "m/s"],
    ];
    for (const [value, label] of units) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.windUnit = value;
      button.textContent = label;
      button.setAttribute("aria-pressed", String(value === preferences.windSpeedUnit));
      button.addEventListener("click", () => {
        setWindSpeedUnit(value);
        for (const candidate of windUnitOptions.querySelectorAll<HTMLButtonElement>("button")) {
          candidate.setAttribute("aria-pressed", String(candidate.dataset.windUnit === value));
        }
        if (this.windMode === "live") this.renderLiveWind();
        else if (this.windMode === "forecast") this.renderForecastHour();
      });
      windUnitOptions.append(button);
    }
    windUnitSetting.append(windUnitLegend, windUnitDetail, windUnitOptions);

    const serverSetting = document.createElement("fieldset");
    serverSetting.className = "map-ui-setting map-server-settings";
    const serverLegend = document.createElement("legend");
    serverLegend.textContent = "Server URLs";
    const serverDetail = document.createElement("small");
    serverDetail.textContent = "Saved only in this browser.";
    const form = document.createElement("form");
    form.className = "map-server-form";
    form.noValidate = true;

    const urlField = (
      labelText: string,
      detailText: string,
      value: string,
      name: string,
    ): HTMLInputElement => {
      const label = document.createElement("label");
      label.className = "map-server-field";
      const labelTextElement = document.createElement("strong");
      labelTextElement.textContent = labelText;
      const detail = document.createElement("small");
      detail.textContent = detailText;
      const input = document.createElement("input");
      input.type = "text";
      input.inputMode = "url";
      input.name = name;
      input.autocomplete = "off";
      input.autocapitalize = "none";
      input.spellcheck = false;
      input.value = value;
      input.setAttribute("aria-label", labelText);
      label.append(labelTextElement, detail, input);
      form.append(label);
      return input;
    };

    const mapServerInput = urlField(
      "Map server",
      "Catalogue root; leave empty to keep the cached and stored map state",
      chartServer() ?? "",
      "map-server",
    );
    const smhiInput = urlField(
      "SMHI observations",
      "Base URL for latest station observations",
      preferences.smhiBaseUrl,
      "smhi-url",
    );
    const openMeteoInput = urlField(
      "Open-Meteo forecast",
      "Forecast API endpoint",
      preferences.openMeteoUrl,
      "open-meteo-url",
    );
    const aisInput = urlField(
      "AIS server",
      "Basic Auth may be included as https://user:password@host/",
      preferences.aisServerUrl,
      "ais-server-url",
    );

    const ownMmsiLabel = document.createElement("label");
    ownMmsiLabel.className = "map-server-field";
    const ownMmsiTitle = document.createElement("strong");
    ownMmsiTitle.textContent = "Own vessel MMSI";
    const ownMmsiDetail = document.createElement("small");
    ownMmsiDetail.textContent = "Optional; prevents your own AIS transmitter from raising a collision warning";
    const ownMmsiInput = document.createElement("input");
    ownMmsiInput.type = "text";
    ownMmsiInput.inputMode = "numeric";
    ownMmsiInput.autocomplete = "off";
    ownMmsiInput.value = preferences.ownMmsi;
    ownMmsiInput.setAttribute("aria-label", "Own vessel MMSI");
    ownMmsiLabel.append(ownMmsiTitle, ownMmsiDetail, ownMmsiInput);
    form.append(ownMmsiLabel);

    const credentialWarning = document.createElement("p");
    credentialWarning.className = "map-settings-warning";
    credentialWarning.textContent =
      "An AIS password in the URL is stored unencrypted in this browser. Klint removes it from the request URL and sends it as a Basic Authorization header.";
    const saveButton = document.createElement("button");
    saveButton.className = "button map-settings-save";
    saveButton.type = "submit";
    saveButton.textContent = "Save server settings";
    const status = document.createElement("p");
    status.className = "map-menu-note map-settings-status";
    status.hidden = true;
    status.setAttribute("aria-live", "polite");
    form.append(credentialWarning, saveButton, status);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void (async () => {
        status.hidden = true;
        saveButton.disabled = true;
        saveButton.textContent = "Saving…";
        let providersSaved = false;
        try {
          const ownMmsi = normaliseOwnMmsi(ownMmsiInput.value);
          const previousMapServer = chartServer();
          const mapValue = mapServerInput.value.trim();
          const nextMapServer = mapValue ? normaliseChartServer(mapValue) : null;
          const urls = setProviderUrls({
            smhiBaseUrl: smhiInput.value,
            openMeteoUrl: openMeteoInput.value,
            aisServerUrl: aisInput.value,
          });
          setOwnMmsi(ownMmsi);
          providersSaved = true;
          smhiInput.value = urls.smhiBaseUrl;
          openMeteoInput.value = urls.openMeteoUrl;
          aisInput.value = urls.aisServerUrl;
          ownMmsiInput.value = ownMmsi;
          this.refreshDataProviders();

          if (nextMapServer !== previousMapServer) {
            await this.options.onMapServerChange(nextMapServer);
            return;
          }
          status.textContent = "Server settings saved.";
          status.dataset.tone = "success";
          status.hidden = false;
        } catch (error) {
          status.textContent = `${providersSaved ? "Provider URLs were saved, but the map server was not changed: " : ""}${error instanceof Error ? error.message : "The server settings could not be saved."}`;
          status.dataset.tone = "error";
          status.hidden = false;
        } finally {
          saveButton.disabled = false;
          saveButton.textContent = "Save server settings";
        }
      })();
    });
    serverSetting.append(serverLegend, serverDetail, form);

    settings.append(sizeSetting, darkSetting, windUnitSetting, serverSetting);
    return settings;
  }

  private refreshDataProviders(): void {
    this.windReadings = [];
    this.forecastSeries = [];
    if (this.windMode === "live") this.setWindMode("live");
    else if (this.windMode === "forecast") this.setWindMode("forecast");

    const available = aisDataAvailable();
    for (const input of this.aisModeInputs) input.disabled = !available;
    this.updateAisExplanation();
    if (this.aisMode !== "off") this.setAisMode(this.aisMode);
  }

  private buildWindSettings(): HTMLElement {
    const settings = document.createElement("div");
    settings.className = "wind-settings";

    const modeSetting = document.createElement("fieldset");
    modeSetting.className = "wind-mode-setting";
    const legend = document.createElement("legend");
    legend.textContent = "Display on map";
    const options = document.createElement("div");
    options.className = "wind-mode-options";
    const modes: Array<[WindMode, string, string]> = [
      ["live", "Live observations", "Latest SMHI station readings"],
      ["forecast", "Forecast", "DMI HARMONIE with ECMWF extension"],
      ["off", "Off", "Hide wind arrows"],
    ];
    for (const [value, label, detail] of modes) {
      const option = document.createElement("label");
      option.className = "wind-mode-option";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "wind-mode";
      input.value = value;
      input.checked = value === "live";
      input.disabled = true;
      input.addEventListener("change", () => {
        if (input.checked) this.setWindMode(value);
      });
      this.windModeInputs.push(input);
      const text = document.createElement("span");
      const name = document.createElement("strong");
      name.textContent = label;
      const note = document.createElement("small");
      note.textContent = detail;
      text.append(name, note);
      option.append(input, text);
      options.append(option);
    }
    modeSetting.append(legend, options);

    this.forecastDateSetting.className = "wind-date-setting";
    this.forecastDateSetting.hidden = true;
    const dateText = document.createElement("span");
    const dateLabel = document.createElement("strong");
    dateLabel.textContent = "Forecast date";
    const dateDetail = document.createElement("small");
    dateDetail.textContent = "Available for today and the following 14 days";
    dateText.append(dateLabel, dateDetail);
    this.forecastDateInput.type = "date";
    this.forecastDateInput.min = dateFromNow(0);
    this.forecastDateInput.max = dateFromNow(14);
    this.forecastDateInput.value = dateFromNow(0);
    this.forecastDateInput.addEventListener("change", () => {
      if (this.windMode !== "forecast") return;
      this.windTimeSlider.value =
        this.forecastDateInput.value === dateFromNow(0)
          ? String(new Date().getHours())
          : "12";
      this.updateWindTimeLabel();
      void this.loadForecastWind();
    });
    this.forecastDateSetting.append(dateText, this.forecastDateInput);

    const explanation = document.createElement("p");
    explanation.className = "map-menu-note wind-explanation";
    explanation.textContent =
      "Live arrows are interpolated from the nearest recent stations. Forecast arrows sample the model across the visible map.";
    this.windStatus.className = "map-menu-note wind-menu-status";
    this.windStatus.hidden = true;
    this.windStatus.setAttribute("aria-live", "polite");
    settings.append(
      modeSetting,
      this.forecastDateSetting,
      explanation,
      this.windStatus,
    );
    return settings;
  }

  private buildWindTimeControl(): void {
    this.windTimeControl.className = "wind-time-control";
    this.windTimeControl.hidden = true;
    this.windTimeControl.setAttribute("aria-label", "Forecast time");
    const header = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = "Forecast wind";
    this.windTimeLabel.className = "wind-time-label";
    header.append(title, this.windTimeLabel);
    this.windTimeSlider.type = "range";
    this.windTimeSlider.min = "0";
    this.windTimeSlider.max = "23";
    this.windTimeSlider.step = "1";
    this.windTimeSlider.value = String(new Date().getHours());
    this.windTimeSlider.setAttribute("aria-label", "Forecast hour");
    this.windTimeSlider.addEventListener("input", () => {
      this.updateWindTimeLabel();
      this.renderForecastHour();
    });
    const endpoints = document.createElement("div");
    endpoints.className = "wind-time-endpoints";
    const start = document.createElement("span");
    start.textContent = "00:00";
    const end = document.createElement("span");
    end.textContent = "23:59";
    endpoints.append(start, end);
    this.windTimeControl.append(header, this.windTimeSlider, endpoints);
    this.updateWindTimeLabel();
  }

  private setWindMode(mode: WindMode): void {
    this.windMode = mode;
    for (const input of this.windModeInputs) input.checked = input.value === mode;
    this.forecastDateSetting.hidden = mode !== "forecast";
    this.windTimeControl.hidden = mode !== "forecast";
    this.windAbort?.abort();
    this.windAbort = null;
    this.windRequestSequence++;
    if (this.liveWindRefreshTimer !== null) {
      window.clearTimeout(this.liveWindRefreshTimer);
      this.liveWindRefreshTimer = null;
    }
    if (mode === "off") {
      this.windReadings = [];
      this.forecastSeries = [];
      this.windStatus.hidden = true;
      this.windOverlay?.clear();
      return;
    }
    if (mode === "live") {
      void this.loadLiveWind();
      return;
    }
    this.windTimeSlider.value =
      this.forecastDateInput.value === dateFromNow(0)
        ? String(new Date().getHours())
        : "12";
    this.updateWindTimeLabel();
    void this.loadForecastWind();
  }

  private setWindMenuStatus(message: string, tone?: "error"): void {
    this.windStatus.textContent = message;
    this.windStatus.hidden = false;
    if (tone) this.windStatus.dataset.tone = tone;
    else delete this.windStatus.dataset.tone;
  }

  private async loadLiveWind(): Promise<void> {
    if (this.windMode !== "live" || !this.windOverlay) return;
    const sequence = ++this.windRequestSequence;
    this.windAbort?.abort();
    const controller = new AbortController();
    this.windAbort = controller;
    this.setWindMenuStatus(
      this.windReadings.length ? "Refreshing SMHI observations…" : "Loading SMHI observations…",
    );
    if (!this.windReadings.length) this.windOverlay.showLoading("Live wind");
    try {
      this.windReadings = await fetchLatestWindReadings(controller.signal);
      if (sequence !== this.windRequestSequence || this.windMode !== "live") return;
      if (this.activeRouteLog) {
        this.routeLogWindReadings = this.windReadings;
        this.setRouteLogWindLabel("wind ready");
      }
      this.renderLiveWind();
    } catch (error) {
      if (controller.signal.aborted || sequence !== this.windRequestSequence) return;
      const message = errorMessage(error);
      this.setWindMenuStatus(message, "error");
      this.windOverlay.showError("Live wind", message);
    } finally {
      if (sequence === this.windRequestSequence) this.windAbort = null;
      if (!this.stopped && this.windMode === "live") {
        this.liveWindRefreshTimer = window.setTimeout(
          () => void this.loadLiveWind(),
          LIVE_WIND_REFRESH_MS,
        );
      }
    }
  }

  private renderLiveWind(): void {
    if (
      this.windMode !== "live" ||
      !this.handle ||
      !this.windOverlay ||
      !this.windReadings.length
    ) {
      return;
    }
    const samples: WindDisplaySample[] = windGridLocations(this.handle.map)
      .map((location) => interpolateWind(this.windReadings, location))
      .filter(
        (sample): sample is NonNullable<typeof sample> =>
          sample !== null && sample.nearestDistanceKm <= MAX_LIVE_STATION_DISTANCE_KM,
      );
    const center = this.handle.map.getCenter();
    const centerWind = interpolateWind(this.windReadings, {
      longitude: center.lng,
      latitude: center.lat,
    });
    if (!centerWind || centerWind.nearestDistanceKm > MAX_LIVE_STATION_DISTANCE_KM) {
      const message = "No recent SMHI wind station is close enough to this view.";
      this.setWindMenuStatus(message, "error");
      this.windOverlay.showError("Live wind", message);
      return;
    }
    const observationTime = new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(centerWind.observedAt));
    this.windOverlay.showData(samples, {
      modeLabel: "Live wind",
      speedKnots: centerWind.speedKnots,
      direction: centerWind.direction,
      detail: `Observed ${observationTime} · near ${centerWind.nearestStation}`,
      providerLabel: "SMHI data · interpolated",
      providerUrl: "https://opendata.smhi.se/metobs/introduction",
      speedUnit: uiPreferences().windSpeedUnit,
    });
    this.setWindMenuStatus(
      `Showing ${samples.length} interpolated arrows from ${this.windReadings.length} recent stations.`,
    );
  }

  private validForecastDate(): string | null {
    const date = this.forecastDateInput.value;
    return date && date >= this.forecastDateInput.min && date <= this.forecastDateInput.max
      ? date
      : null;
  }

  private async loadForecastWind(): Promise<void> {
    if (this.windMode !== "forecast" || !this.handle || !this.windOverlay) return;
    const date = this.validForecastDate();
    if (!date) {
      const message = "Choose a forecast date in the available range.";
      this.windTimeControl.hidden = true;
      this.forecastSeries = [];
      this.windOverlay.clear();
      this.setWindMenuStatus(message, "error");
      return;
    }
    this.windTimeControl.hidden = false;
    const locations = windGridLocations(this.handle.map);
    const sequence = ++this.windRequestSequence;
    this.windAbort?.abort();
    const controller = new AbortController();
    this.windAbort = controller;
    this.forecastSeries = [];
    this.setWindMenuStatus("Loading the DMI wind forecast…");
    this.windOverlay.showLoading("Forecast wind");
    try {
      this.forecastSeries = await fetchForecastWind(
        locations,
        date,
        forecastTimeZone(),
        controller.signal,
      );
      if (sequence !== this.windRequestSequence || this.windMode !== "forecast") return;
      this.renderForecastHour();
    } catch (error) {
      if (controller.signal.aborted || sequence !== this.windRequestSequence) return;
      const message = errorMessage(error);
      this.setWindMenuStatus(message, "error");
      this.windOverlay.showError("Forecast wind", message);
    } finally {
      if (sequence === this.windRequestSequence) this.windAbort = null;
    }
  }

  private renderForecastHour(): void {
    if (
      this.windMode !== "forecast" ||
      !this.windOverlay ||
      !this.forecastSeries.length
    ) {
      return;
    }
    const hour = Math.max(0, Math.min(23, Number(this.windTimeSlider.value)));
    const samples: WindDisplaySample[] = this.forecastSeries.flatMap((series) => {
      const sample = series.samples[hour];
      return sample
        ? [{
            longitude: series.longitude,
            latitude: series.latitude,
            speedKnots: sample.speedKnots,
            direction: sample.direction,
          }]
        : [];
    });
    const centerSeries = this.forecastSeries[Math.floor(this.forecastSeries.length / 2)];
    const centerWind = centerSeries?.samples[hour];
    if (!centerWind || !samples.length) {
      const message = `No forecast is available for ${String(hour).padStart(2, "0")}:00.`;
      this.setWindMenuStatus(message, "error");
      this.windOverlay.showError("Forecast wind", message);
      return;
    }
    const time = `${String(hour).padStart(2, "0")}:00`;
    this.windOverlay.showData(samples, {
      modeLabel: "Forecast wind",
      speedKnots: centerWind.speedKnots,
      direction: centerWind.direction,
      detail: `${this.forecastDateInput.value} ${time} · DMI HARMONIE/ECMWF`,
      providerLabel: "Weather data by Open-Meteo",
      providerUrl: "https://open-meteo.com/",
      speedUnit: uiPreferences().windSpeedUnit,
      weather: {
        code: centerWind.weatherCode,
        isDay: centerWind.isDay,
        precipitationMm: centerWind.precipitationMm,
      },
    });
    this.setWindMenuStatus(
      `Showing ${samples.length} model points for ${this.forecastDateInput.value} at ${time}.`,
    );
  }

  private updateWindTimeLabel(): void {
    const hour = Math.max(0, Math.min(23, Number(this.windTimeSlider.value)));
    const time = `${String(hour).padStart(2, "0")}:00`;
    const date = this.forecastDateInput.value;
    let dateLabel = date;
    if (date) {
      try {
        dateLabel = new Intl.DateTimeFormat(undefined, {
          weekday: "short",
          day: "numeric",
          month: "short",
        }).format(new Date(`${date}T12:00:00`));
      } catch {
        // The ISO date is still understandable if locale formatting is unavailable.
      }
    }
    this.windTimeLabel.value = `${dateLabel} · ${time}`;
    this.windTimeSlider.setAttribute("aria-valuetext", `${dateLabel} ${time}`);
  }

  private buildAisSettings(): HTMLElement {
    const settings = document.createElement("div");
    settings.className = "ais-settings";

    const modeSetting = document.createElement("fieldset");
    modeSetting.className = "ais-mode-setting";
    const legend = document.createElement("legend");
    legend.textContent = "Display on map";
    const options = document.createElement("div");
    options.className = "ais-mode-options";
    const modes: Array<[AisMode, string, string]> = [
      ["live", "Live vessels", "AIS speed/course with fix-to-fix fallback"],
      ["historical", "Typical traffic", "Same local time across previous days"],
      ["off", "Off", "Hide AIS traffic and selected routes"],
    ];
    for (const [value, label, detail] of modes) {
      const option = document.createElement("label");
      option.className = "ais-mode-option";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "ais-mode";
      input.value = value;
      input.checked = value === "off";
      input.disabled = true;
      input.addEventListener("change", () => {
        if (input.checked) this.setAisMode(value);
      });
      this.aisModeInputs.push(input);
      const text = document.createElement("span");
      const name = document.createElement("strong");
      name.textContent = label;
      const note = document.createElement("small");
      note.textContent = detail;
      text.append(name, note);
      option.append(input, text);
      options.append(option);
    }
    modeSetting.append(legend, options);

    const historySetting = document.createElement("label");
    historySetting.className = "ais-history-setting";
    const historyText = document.createElement("span");
    const historyLabel = document.createElement("strong");
    historyLabel.textContent = "Previous days";
    const historyDetail = document.createElement("small");
    historyDetail.textContent = "Paths fade across a ±60 minute window";
    historyText.append(historyLabel, historyDetail);
    for (const days of [1, 3, 7]) {
      const option = document.createElement("option");
      option.value = String(days);
      option.textContent = String(days);
      option.selected = days === 3;
      this.aisHistoryDaysSelect.append(option);
    }
    this.aisHistoryDaysSelect.setAttribute("aria-label", "Previous days to compare");
    this.aisHistoryDaysSelect.addEventListener("change", () => {
      this.updateAisTimeLabel();
      if (this.aisMode === "historical") void this.loadHistoricalAis();
    });
    historySetting.append(historyText, this.aisHistoryDaysSelect);

    this.aisExplanation.className = "map-menu-note ais-explanation";
    this.updateAisExplanation();
    this.aisStatus.className = "map-menu-note ais-menu-status";
    this.aisStatus.hidden = true;
    this.aisStatus.setAttribute("aria-live", "polite");
    settings.append(modeSetting, historySetting, this.aisExplanation);
    if (aisDataSource() === "mock") {
      const sampleAreaButton = document.createElement("button");
      sampleAreaButton.type = "button";
      sampleAreaButton.className = "map-menu-action ais-sample-area-button";
      sampleAreaButton.textContent = "Show Stockholm sample";
      sampleAreaButton.addEventListener("click", () => this.showAisSampleArea());
      settings.append(sampleAreaButton);
    }
    settings.append(this.aisStatus);
    return settings;
  }

  private updateAisExplanation(): void {
    this.aisExplanation.textContent = aisDataAvailable()
      ? `${aisDataSourceLabel()}. Historical paths are observations, not predicted routes.${aisDataSource() === "mock" ? " Sample positions are around Stockholm and the central Baltic." : ""}`
      : "Set an AIS server URL under Settings to enable this layer.";
    if (aisDataSource() === "mock") this.aisExplanation.dataset.tone = "sample";
    else delete this.aisExplanation.dataset.tone;
  }

  private buildAisTimeControl(): void {
    this.aisTimeControl.className = "ais-time-control";
    this.aisTimeControl.hidden = true;
    this.aisTimeControl.setAttribute("aria-label", "Historical AIS time of day");
    const header = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = "Typical traffic";
    this.aisTimeLabel.className = "ais-time-label";
    header.append(title, this.aisTimeLabel);
    this.aisTimeSlider.type = "range";
    this.aisTimeSlider.min = "0";
    this.aisTimeSlider.max = "95";
    this.aisTimeSlider.step = "1";
    const now = new Date();
    const currentSlot = Math.round((now.getHours() * 60 + now.getMinutes()) / 15) % 96;
    this.aisTimeSlider.value = String(aisDataSource() === "mock" ? 32 : currentSlot);
    this.aisTimeSlider.setAttribute("aria-label", "Historical traffic time of day");
    this.aisTimeSlider.addEventListener("input", () => {
      this.updateAisTimeLabel();
      if (this.aisMode !== "historical") return;
      if (this.aisTimeTimer !== null) window.clearTimeout(this.aisTimeTimer);
      this.aisTimeTimer = window.setTimeout(() => {
        this.aisTimeTimer = null;
        void this.loadHistoricalAis();
      }, 300);
    });
    const endpoints = document.createElement("div");
    endpoints.className = "ais-time-endpoints";
    const start = document.createElement("span");
    start.textContent = "00:00";
    const end = document.createElement("span");
    end.textContent = "23:45";
    endpoints.append(start, end);
    const legend = document.createElement("small");
    legend.className = "ais-time-legend";
    legend.textContent = "Paths fade ±60 min · brighter overlaps mean more recurring traffic";
    this.aisTimeControl.append(header, this.aisTimeSlider, endpoints, legend);
    this.updateAisTimeLabel();
  }

  private aisTimeOfDay(): string {
    const totalMinutes = Math.max(0, Math.min(95, Number(this.aisTimeSlider.value))) * 15;
    return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
  }

  private updateAisTimeLabel(): void {
    const time = this.aisTimeOfDay();
    const days = Number(this.aisHistoryDaysSelect.value || 3);
    this.aisTimeLabel.value = `${time} · ${days} ${days === 1 ? "day" : "days"}`;
    this.aisTimeSlider.setAttribute(
      "aria-valuetext",
      `${time}, compared across ${days} previous ${days === 1 ? "day" : "days"}`,
    );
  }

  private setAisMode(mode: AisMode): void {
    this.aisMode = mode;
    for (const input of this.aisModeInputs) input.checked = input.value === mode;
    this.aisTimeControl.hidden = mode !== "historical";
    this.aisTrafficAbort?.abort();
    this.aisTrafficAbort = null;
    this.aisTypicalAbort?.abort();
    this.aisTypicalAbort = null;
    this.collisionAisAbort?.abort();
    this.collisionAisAbort = null;
    this.collisionAisSequence++;
    this.aisRequestSequence++;
    if (this.liveAisRefreshTimer !== null) {
      window.clearTimeout(this.liveAisRefreshTimer);
      this.liveAisRefreshTimer = null;
    }
    this.clearAisSelection();
    this.liveAisTraffic = null;
    this.collisionAisTraffic = null;
    this.collisionTypicalAisTraffic = null;
    this.collisionAisLastRequestAt = 0;
    this.hideCollisionWarning();
    if (mode === "off") {
      this.aisStatus.hidden = true;
      this.aisOverlay?.clearTraffic();
      return;
    }
    if (!aisDataAvailable()) {
      this.setAisStatus("AIS server is not configured.", "error");
      this.aisOverlay?.clearTraffic();
      return;
    }
    if (mode === "live") void this.loadLiveAis();
    else void this.loadHistoricalAis();
  }

  private setAisStatus(message: string, tone?: "error"): void {
    this.aisStatus.textContent = message;
    this.aisStatus.hidden = false;
    if (tone) this.aisStatus.dataset.tone = tone;
    else delete this.aisStatus.dataset.tone;
  }

  private fitMockAisSampleOnce(): boolean {
    if (aisDataSource() !== "mock" || this.aisMockSampleFitted) return false;
    const fitted = this.aisOverlay?.fitTraffic() ?? false;
    if (fitted) this.aisMockSampleFitted = true;
    return fitted;
  }

  private showAisSampleArea(): void {
    if (aisDataSource() !== "mock") return;
    if (this.aisOverlay?.fitTraffic()) {
      this.aisMockSampleFitted = true;
      this.setMenuOpen(false);
      return;
    }
    this.aisMockSampleFitted = false;
    if (this.aisMode === "off") this.setAisMode("live");
    else if (this.aisMode === "live") void this.loadLiveAis();
    else void this.loadHistoricalAis();
  }

  private aisBounds(): AisBounds | null {
    if (!this.handle) return null;
    const bounds = this.handle.map.getBounds();
    return {
      west: bounds.getWest(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      north: bounds.getNorth(),
    };
  }

  private liveTypicalTimeOfDay(): string {
    const now = new Date();
    const slotMinutes = Math.floor((now.getHours() * 60 + now.getMinutes()) / 15) * 15;
    return `${String(Math.floor(slotMinutes / 60)).padStart(2, "0")}:${String(slotMinutes % 60).padStart(2, "0")}`;
  }

  private async typicalTrafficForLiveAis(
    bounds: AisBounds,
    signal: AbortSignal,
  ): Promise<AisPointCollection | null> {
    const timeOfDay = this.liveTypicalTimeOfDay();
    const key = [
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north,
    ].map((value) => value.toFixed(2)).concat(timeOfDay).join(":");
    if (
      this.typicalAisCache?.key === key &&
      this.typicalAisCache.expiresAt > Date.now()
    ) {
      return this.typicalAisCache.collection;
    }
    try {
      const collection = await fetchTypicalAisSnapshot(
        {
          bounds,
          timeOfDay,
          timezone: forecastTimeZone(),
          days: 7,
          windowMinutes: 60,
        },
        signal,
      );
      this.typicalAisCache = {
        key,
        expiresAt: Date.now() + TYPICAL_AIS_CACHE_MS,
        collection,
      };
      return collection;
    } catch {
      return null;
    }
  }

  private async loadLiveAis(): Promise<void> {
    const bounds = this.aisBounds();
    if (this.aisMode !== "live" || !bounds || !this.aisOverlay) return;
    const sequence = ++this.aisRequestSequence;
    this.aisTrafficAbort?.abort();
    this.aisTypicalAbort?.abort();
    const controller = new AbortController();
    const typicalController = new AbortController();
    this.aisTrafficAbort = controller;
    this.aisTypicalAbort = typicalController;
    this.setAisStatus("Loading live AIS positions…");
    const typicalRequest = this.typicalTrafficForLiveAis(bounds, typicalController.signal);
    try {
      const collection = await fetchLatestAis(bounds, controller.signal);
      if (sequence !== this.aisRequestSequence || this.aisMode !== "live") return;
      this.liveAisTraffic = collection;
      this.typicalAisTraffic = null;
      this.aisOverlay.showTraffic(collection, "live");
      this.updateCollisionWarning();
      void this.loadCollisionAis();
      this.fitMockAisSampleOnce();
      const suffix = aisDataSource() === "mock" ? " · Stockholm development sample" : "";
      this.setAisStatus(
        `Showing ${collection.count} recent ${collection.count === 1 ? "vessel" : "vessels"}${suffix}.`,
      );
      void typicalRequest.then((typicalTraffic) => {
        if (
          typicalController.signal.aborted ||
          sequence !== this.aisRequestSequence ||
          this.aisMode !== "live"
        ) return;
        this.typicalAisTraffic = typicalTraffic;
        this.updateCollisionWarning();
      }).finally(() => {
        if (this.aisTypicalAbort === typicalController) this.aisTypicalAbort = null;
      });
    } catch (error) {
      typicalController.abort();
      if (controller.signal.aborted || sequence !== this.aisRequestSequence) return;
      this.liveAisTraffic = null;
      this.hideCollisionWarning();
      const message = error instanceof Error ? error.message : "AIS data could not be loaded.";
      this.setAisStatus(message, "error");
    } finally {
      if (sequence === this.aisRequestSequence) this.aisTrafficAbort = null;
      if (
        sequence === this.aisRequestSequence &&
        !this.stopped && this.aisMode === "live" && aisDataSource() === "api"
      ) {
        this.liveAisRefreshTimer = window.setTimeout(
          () => void this.loadLiveAis(),
          LIVE_AIS_REFRESH_MS,
        );
      }
    }
  }

  private async loadHistoricalAis(): Promise<void> {
    const bounds = this.aisBounds();
    if (this.aisMode !== "historical" || !bounds || !this.aisOverlay) return;
    const sequence = ++this.aisRequestSequence;
    this.aisTrafficAbort?.abort();
    const controller = new AbortController();
    this.aisTrafficAbort = controller;
    const days = Number(this.aisHistoryDaysSelect.value || 3);
    const timeOfDay = this.aisTimeOfDay();
    this.setAisStatus(`Loading traffic near ${timeOfDay}…`);
    try {
      const collection = await fetchTypicalAis(
        {
          bounds,
          timeOfDay,
          timezone: forecastTimeZone(),
          days,
          windowMinutes: 60,
        },
        controller.signal,
      );
      if (sequence !== this.aisRequestSequence || this.aisMode !== "historical") return;
      this.liveAisTraffic = null;
      this.hideCollisionWarning();
      const pathSegments = this.aisOverlay.showTraffic(collection, "historical");
      this.fitMockAisSampleOnce();
      const daysWithData = collection.day_counts
        ? Object.values(collection.day_counts).filter((count) => count > 0).length
        : new Set(collection.features.map((feature) => feature.properties.sample_date)).size;
      const shownTime = collection.time_of_day ?? timeOfDay;
      const suffix = aisDataSource() === "mock" ? " · Stockholm development sample" : "";
      this.setAisStatus(
        `${collection.count} sampled fixes form ${pathSegments} faded path segments near ${shownTime} across ${daysWithData} ${daysWithData === 1 ? "day" : "days"}${suffix}.`,
      );
    } catch (error) {
      if (controller.signal.aborted || sequence !== this.aisRequestSequence) return;
      const message = error instanceof Error ? error.message : "Historical AIS data could not be loaded.";
      this.setAisStatus(message, "error");
    } finally {
      if (sequence === this.aisRequestSequence) this.aisTrafficAbort = null;
    }
  }

  private selectAisVessel(vessel: AisVesselSelection): void {
    this.selectedAisVessel = vessel;
    this.aisOverlay?.select(vessel, this.aisTrackDays);
    void this.loadSelectedAisTrack();
  }

  private setAisTrackDays(days: number): void {
    this.aisTrackDays = [1, 3, 7].includes(days) ? days : 3;
    if (!this.selectedAisVessel) return;
    this.aisOverlay?.select(this.selectedAisVessel, this.aisTrackDays);
    void this.loadSelectedAisTrack();
  }

  private async loadSelectedAisTrack(): Promise<void> {
    const vessel = this.selectedAisVessel;
    if (!vessel || !this.aisOverlay) return;
    const sequence = ++this.aisTrackSequence;
    this.aisTrackAbort?.abort();
    const controller = new AbortController();
    this.aisTrackAbort = controller;
    try {
      const collection = await fetchAisTrack(
        vessel.mmsi,
        this.aisTrackDays,
        controller.signal,
      );
      if (sequence !== this.aisTrackSequence || vessel !== this.selectedAisVessel) return;
      this.aisOverlay.showTrack(collection);
    } catch (error) {
      if (controller.signal.aborted || sequence !== this.aisTrackSequence) return;
      this.aisOverlay.showTrackError(
        error instanceof Error ? error.message : "Vessel route could not be loaded.",
      );
    } finally {
      if (sequence === this.aisTrackSequence) this.aisTrackAbort = null;
    }
  }

  private clearAisSelection(): void {
    this.aisTrackAbort?.abort();
    this.aisTrackAbort = null;
    this.aisTrackSequence++;
    this.selectedAisVessel = null;
    this.aisOverlay?.clearSelection();
  }

  private updateInstallButton(state: PwaInstallState): void {
    if (!this.installButton) return;
    this.installButton.textContent = state === "installed" ? "Klint is installed" : "Install Klint";
    this.installButton.disabled = state === "installed";
    this.installButton.dataset.installState = state;
    if (state === "available") this.installNote.hidden = true;
  }

  private async installApp(): Promise<void> {
    if (!this.installButton) return;
    this.installButton.disabled = true;
    try {
      const result = await requestPwaInstall();
      this.installNote.textContent = result.message;
      this.installNote.hidden = false;
      if (result.outcome === "manual" || result.outcome === "dismissed") {
        this.installButton.disabled = false;
      }
    } catch (error) {
      this.installNote.textContent =
        error instanceof Error ? error.message : "Klint could not be installed.";
      this.installNote.hidden = false;
      this.installButton.disabled = false;
    }
  }

  private renderSavedRoutes(): void {
    const routes = loadRoutes();
    if (!routes.length) {
      const empty = document.createElement("p");
      empty.className = "map-menu-note";
      empty.textContent = "No routes are saved on this device yet.";
      this.savedRouteList.replaceChildren(empty);
      return;
    }
    const rows = routes.map((route) => {
      const row = document.createElement("article");
      row.className = "saved-route";
      const text = document.createElement("div");
      const name = document.createElement("strong");
      name.textContent = route.name;
      const detail = document.createElement("small");
      const nauticalMiles = kilometresToNauticalMiles(routeDistanceKm(route.points));
      const updated = new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(route.updatedAt));
      detail.textContent =
        `${route.points.length} waypoints · ${nauticalMiles.toFixed(2)} NM · ${updated}`;
      text.append(name, detail);
      const actions = document.createElement("div");
      actions.className = "saved-route-actions";
      const load = document.createElement("button");
      load.type = "button";
      load.textContent = "Load";
      load.addEventListener("click", () => {
        this.setMenuOpen(false);
        this.openRoute(route, route.id);
      });
      const remove = document.createElement("button");
      remove.type = "button";
      remove.textContent = "Delete";
      remove.addEventListener("click", () => this.removeSavedRoute(route));
      actions.append(load, remove);
      row.append(text, actions);
      return row;
    });
    this.savedRouteList.replaceChildren(...rows);
  }

  private removeSavedRoute(route: StoredRoute): void {
    if (!window.confirm(`Delete “${route.name}” from this device?`)) return;
    try {
      deleteRoute(route.id);
      if (this.activeRouteId === route.id) this.activeRouteId = null;
      this.renderSavedRoutes();
    } catch (error) {
      const message = document.createElement("p");
      message.className = "map-menu-note";
      message.dataset.tone = "error";
      message.textContent =
        error instanceof Error ? error.message : "The route could not be deleted.";
      this.savedRouteList.prepend(message);
    }
  }

  private async renderRouteLogs(): Promise<void> {
    const sequence = ++this.routeLogListSequence;
    const loading = document.createElement("p");
    loading.className = "map-menu-note";
    loading.textContent = "Loading route logs…";
    this.routeLogList.replaceChildren(loading);
    try {
      const logs = await loadRouteLogs();
      if (sequence !== this.routeLogListSequence) return;
      if (!logs.length) {
        const empty = document.createElement("p");
        empty.className = "map-menu-note";
        empty.textContent = "No route logs are stored on this device yet.";
        this.routeLogList.replaceChildren(empty);
        return;
      }
      const rows = logs.map((log) => {
        const active = this.activeRouteLog?.id === log.id;
        const row = document.createElement("article");
        row.className = "route-log-entry";
        row.dataset.selected = String(this.viewedRouteLogId === log.id);
        const title = document.createElement("strong");
        title.textContent = routeLogDate(log.startedAt);
        const detail = document.createElement("small");
        const duration = active
          ? "Recording now"
          : routeLogDuration(log.startedAt, log.endedAt);
        detail.textContent =
          `${log.sampleCount} GPS fix${log.sampleCount === 1 ? "" : "es"} · ${duration}`;
        const actions = document.createElement("div");
        actions.className = "route-log-entry-actions";
        const view = document.createElement("button");
        view.type = "button";
        view.textContent = active ? "Recording" : "View";
        view.disabled = active || log.sampleCount === 0;
        if (log.sampleCount === 0) view.title = "This log has no GPS positions";
        view.addEventListener("click", () => void this.viewRouteLog(log));
        const remove = document.createElement("button");
        remove.type = "button";
        remove.textContent = "Delete";
        remove.disabled = active;
        remove.addEventListener("click", () => void this.removeRouteLog(log));
        actions.append(view, remove);
        row.append(title, detail, actions);
        return row;
      });
      this.routeLogList.replaceChildren(...rows);
    } catch (error) {
      if (sequence !== this.routeLogListSequence) return;
      const message = document.createElement("p");
      message.className = "map-menu-note";
      message.dataset.tone = "error";
      message.textContent =
        error instanceof Error ? error.message : "Stored route logs could not be read.";
      this.routeLogList.replaceChildren(message);
    }
  }

  private async viewRouteLog(log: RouteLog): Promise<void> {
    if (!this.routeLogOverlay || this.activeRouteLog?.id === log.id) return;
    try {
      const samples = (await loadRouteLogSamples(log.id))
        .sort((a, b) => a.recordedAt - b.recordedAt);
      if (!samples.length) throw new Error("This route log contains no GPS positions.");
      this.viewedRouteLogId = log.id;
      this.routeLogOverlay.show(samples);
      this.routeLogViewTitle.textContent = routeLogDate(log.startedAt);
      const motorFixes = samples.filter((sample) => sample.motorOn).length;
      this.routeLogViewDetail.textContent =
        `${samples.length} fix${samples.length === 1 ? "" : "es"} · ${motorFixes} on motor`;
      this.routeLogViewControl.hidden = false;
      this.fitRouteLog(samples);
      this.setMenuOpen(false);
    } catch (error) {
      const message = document.createElement("p");
      message.className = "map-menu-note";
      message.dataset.tone = "error";
      message.textContent =
        error instanceof Error ? error.message : "The route log could not be displayed.";
      this.routeLogList.prepend(message);
    }
  }

  private fitRouteLog(samples: RouteLogSample[]): void {
    if (!this.handle || !samples.length) return;
    const first: RoutePoint = [samples[0].longitude, samples[0].latitude];
    const hasExtent = samples.some(
      (sample) => sample.longitude !== first[0] || sample.latitude !== first[1],
    );
    if (!hasExtent) {
      this.handle.map.easeTo({
        center: first,
        zoom: Math.max(this.handle.map.getZoom(), 14),
        duration: 550,
      });
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    for (const sample of samples) bounds.extend([sample.longitude, sample.latitude]);
    this.handle.map.fitBounds(bounds, {
      padding: { top: 90, right: 60, bottom: 90, left: 60 },
      maxZoom: 15,
      duration: 550,
    });
  }

  private clearViewedRouteLog(): void {
    if (!this.viewedRouteLogId) return;
    this.viewedRouteLogId = null;
    this.routeLogOverlay?.clear();
    this.routeLogViewControl.hidden = true;
  }

  private async removeRouteLog(log: RouteLog): Promise<void> {
    if (this.activeRouteLog?.id === log.id) return;
    if (!window.confirm(
      `Delete the route log from ${routeLogDate(log.startedAt)} and all of its stored positions?`,
    )) return;
    try {
      await deleteRouteLog(log.id);
      if (this.viewedRouteLogId === log.id) this.clearViewedRouteLog();
      await this.renderRouteLogs();
      void this.updateRouteLogHistory();
    } catch (error) {
      const message = document.createElement("p");
      message.className = "map-menu-note";
      message.dataset.tone = "error";
      message.textContent =
        error instanceof Error ? error.message : "The route log could not be deleted.";
      this.routeLogList.prepend(message);
    }
  }

  private renderLayers(): void {
    const layers = this.handle?.layers ?? [];
    if (!layers.length) {
      const empty = document.createElement("p");
      empty.className = "map-menu-note";
      empty.textContent = "No maps are stored on this device yet.";
      this.layerList.replaceChildren(empty);
      return;
    }

    const rows = layers.map((layer) => {
      const row = document.createElement("label");
      row.className = "map-layer";
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = layer.visible;
      checkbox.addEventListener("change", () => {
        this.handle?.setLayerVisibility(layer.pack.id, checkbox.checked);
      });
      const text = document.createElement("span");
      const name = document.createElement("strong");
      name.textContent = layer.pack.label;
      const detail = document.createElement("small");
      detail.textContent = `${layer.pack.description} · ${formatBytes(packTotalBytes(layer.pack))}`;
      text.append(name, detail);
      row.append(checkbox, text);
      return row;
    });
    this.layerList.replaceChildren(...rows);
  }

  private showMenuPanel(panel: MenuPanel): void {
    this.mainPanel.hidden = panel !== "main";
    this.mapsPanel.hidden = panel !== "maps";
    this.layersPanel.hidden = panel !== "layers";
    this.routesPanel.hidden = panel !== "routes";
    this.savedRoutesPanel.hidden = panel !== "savedRoutes";
    this.routeLogsPanel.hidden = panel !== "routeLogs";
    this.windPanel.hidden = panel !== "wind";
    this.aisPanel.hidden = panel !== "ais";
    this.settingsPanel.hidden = panel !== "settings";
    const target = {
      main: this.mainPanel,
      maps: this.mapsPanel,
      layers: this.layersPanel,
      routes: this.routesPanel,
      savedRoutes: this.savedRoutesPanel,
      routeLogs: this.routeLogsPanel,
      wind: this.windPanel,
      ais: this.aisPanel,
      settings: this.settingsPanel,
    }[panel];
    if (panel === "savedRoutes") this.renderSavedRoutes();
    if (panel === "routeLogs") void this.renderRouteLogs();
    if (panel === "routeLogs") void this.updateRouteLogHistory();
    requestAnimationFrame(() => target.querySelector<HTMLButtonElement>("button")?.focus());
  }

  private setMenuOpen(open: boolean): void {
    this.menu.hidden = !open;
    this.backdrop.hidden = !open;
    const button = this.root.querySelector<HTMLButtonElement>(".map-menu-button");
    button?.setAttribute("aria-expanded", String(open));
    if (open) this.showMenuPanel("main");
  }

  private updateOwnMotion(status: TrackingStatus): void {
    if (
      status.state !== "tracking" ||
      status.longitude === undefined ||
      status.latitude === undefined
    ) return;
    const point: RoutePoint = [status.longitude, status.latitude];
    const timestamp = status.timestamp ?? Date.now();
    const previous = this.previousOwnMotionFix;
    let calculatedSpeed: number | null = null;
    let calculatedCourse: number | null = null;
    let recentPrevious = false;
    if (previous && timestamp > previous.timestamp) {
      const elapsedSeconds = (timestamp - previous.timestamp) / 1000;
      if (elapsedSeconds <= 120) {
        recentPrevious = true;
        const distanceMetres = pointDistanceKm(previous.point, point) * 1000;
        calculatedSpeed = distanceMetres / elapsedSeconds * KNOTS_PER_METRE_PER_SECOND;
        if (distanceMetres >= 2) calculatedCourse = bearingToPoint(previous.point, point);
      }
    }
    const speedKnots = status.speedMetresPerSecond === undefined
      ? calculatedSpeed
      : status.speedMetresPerSecond * KNOTS_PER_METRE_PER_SECOND;
    if (speedKnots !== null && speedKnots >= 0 && speedKnots < 102.3) {
      this.ownMotion = {
        speedKnots,
        courseDeg: status.courseDegrees ?? calculatedCourse ??
          (recentPrevious ? this.ownMotion?.courseDeg ?? null : null),
      };
    } else this.ownMotion = null;
    if (!previous || timestamp > previous.timestamp) {
      this.previousOwnMotionFix = { point, timestamp };
    }
  }

  private ownVesselProjection(): {
    position: RoutePoint;
    courseDeg: number;
    speedKnots: number;
    maxSeconds?: number;
    ownMmsi?: string;
  } | null {
    const status = this.lastTrackingStatus;
    if (
      status.state !== "tracking" ||
      status.longitude === undefined ||
      status.latitude === undefined ||
      !this.ownMotion ||
      Date.now() - (status.timestamp ?? 0) > 2 * 60 * 1000
    ) return null;
    const position: RoutePoint = [status.longitude, status.latitude];
    let courseDeg = this.ownMotion.courseDeg;
    let maxSeconds: number | undefined;
    if (
      this.routeNavigating &&
      this.routeState &&
      this.routeTargetIndex !== null
    ) {
      const target = this.routeState.points[this.routeTargetIndex];
      courseDeg = bearingToPoint(position, target);
      const speedMetresPerSecond = this.ownMotion.speedKnots / KNOTS_PER_METRE_PER_SECOND;
      if (speedMetresPerSecond > 0) {
        maxSeconds = pointDistanceKm(position, target) * 1000 / speedMetresPerSecond;
      }
    }
    if (courseDeg === null) return null;
    return {
      position,
      courseDeg,
      speedKnots: this.ownMotion.speedKnots,
      maxSeconds,
      ownMmsi: uiPreferences().ownMmsi || undefined,
    };
  }

  private collisionAisBounds(own: {
    position: RoutePoint;
    speedKnots: number;
  }): AisBounds {
    // Search independently of the visible map. The radius covers twenty
    // minutes of own travel plus a 50 kn approaching craft and a 2 NM margin.
    const radiusNauticalMiles = 2 +
      (own.speedKnots + COLLISION_ASSUMED_TARGET_SPEED_KNOTS) * 20 / 60;
    const latitudeMargin = radiusNauticalMiles * 1.852 / 111.32;
    const longitudeScale = Math.max(0.1, Math.cos(own.position[1] * Math.PI / 180));
    const longitudeMargin = latitudeMargin / longitudeScale;
    const rawWest = own.position[0] - longitudeMargin;
    const rawEast = own.position[0] + longitudeMargin;
    return {
      west: rawWest < -180 || rawEast > 180 ? -180 : rawWest,
      south: Math.max(-90, own.position[1] - latitudeMargin),
      east: rawWest < -180 || rawEast > 180 ? 180 : rawEast,
      north: Math.min(90, own.position[1] + latitudeMargin),
    };
  }

  private async loadCollisionAis(): Promise<void> {
    const own = this.ownVesselProjection();
    if (this.aisMode !== "live" || !own) return;
    const requestedAt = Date.now();
    if (requestedAt - this.collisionAisLastRequestAt < COLLISION_AIS_REFRESH_MS) return;
    this.collisionAisLastRequestAt = requestedAt;
    const sequence = ++this.collisionAisSequence;
    this.collisionAisAbort?.abort();
    const controller = new AbortController();
    this.collisionAisAbort = controller;
    const bounds = this.collisionAisBounds(own);
    const typicalRequest = this.typicalTrafficForLiveAis(bounds, controller.signal);
    try {
      const traffic = await fetchLatestAis(bounds, controller.signal);
      if (
        controller.signal.aborted || sequence !== this.collisionAisSequence ||
        this.aisMode !== "live"
      ) return;
      this.collisionAisTraffic = traffic;
      this.collisionTypicalAisTraffic = null;
      this.updateCollisionWarning();
      const typicalTraffic = await typicalRequest;
      if (
        controller.signal.aborted || sequence !== this.collisionAisSequence ||
        this.aisMode !== "live"
      ) return;
      this.collisionTypicalAisTraffic = typicalTraffic;
      this.updateCollisionWarning();
    } catch {
      if (!controller.signal.aborted && sequence === this.collisionAisSequence) {
        this.collisionAisTraffic = null;
        this.collisionTypicalAisTraffic = null;
        this.updateCollisionWarning();
      }
    } finally {
      if (this.collisionAisAbort === controller) this.collisionAisAbort = null;
    }
  }

  private updateCollisionWarning(): void {
    if (this.aisMode !== "live") {
      this.hideCollisionWarning();
      return;
    }
    const own = this.ownVesselProjection();
    if (!own) {
      this.hideCollisionWarning();
      return;
    }
    const risks = collisionRisks(
      own,
      this.collisionAisTraffic ?? this.liveAisTraffic,
      typicalCoursesByVessel(this.collisionTypicalAisTraffic ?? this.typicalAisTraffic),
    );
    this.aisOverlay?.showCollisionRisks(risks.map((risk) => risk.vessel.id));
    const nearest = risks[0];
    if (!nearest) {
      this.collisionWarning.hidden = true;
      return;
    }
    const vesselLabel = nearest.vessel.name?.trim() || `MMSI ${nearest.vessel.mmsi}`;
    const timeLabel = nearest.minutesToClosestApproach < 1
      ? "under 1 min"
      : `${Math.round(nearest.minutesToClosestApproach)} min`;
    const otherRisks = risks.length > 1 ? ` · ${risks.length - 1} more risk${risks.length === 2 ? "" : "s"}` : "";
    this.collisionWarningTitle.textContent = risks.length === 1
      ? "Projected collision risk"
      : `${risks.length} projected collision risks`;
    this.collisionWarningDetail.textContent =
      `${vesselLabel}: about ${Math.round(nearest.closestApproachMetres)} m in ${timeLabel} ` +
      `(safety limit ${Math.round(nearest.safetyLimitMetres)} m)${otherRisks}. ` +
      "AIS projection only — maintain a visual and radar watch.";
    this.collisionWarning.hidden = false;
  }

  private hideCollisionWarning(): void {
    this.collisionWarning.hidden = true;
    this.aisOverlay?.showCollisionRisks([]);
  }

  private showStatus(status: TrackingStatus): void {
    this.lastTrackingStatus = status;
    if (status.state === "tracking") {
      this.updateOwnMotion(status);
      void this.loadCollisionAis();
      this.recordRouteLogPosition(status);
      if (this.pendingManOverboard) this.captureManOverboard(status);
    } else if (
      this.pendingManOverboard &&
      (status.state === "denied" || status.state === "error" || status.state === "off")
    ) {
      this.cancelPendingManOverboard(status.message ?? "A current GPS position is unavailable.");
    }
    this.updateRouteNavigation(status);
    this.updateCollisionWarning();
    if (status.message) {
      this.banner.textContent = status.message;
      this.banner.hidden = false;
    } else {
      this.banner.hidden = true;
    }
  }
}
